import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { AwbTracker } from "../components/AwbTracker";
import { IosInstallBanner } from "../components/IosInstallBanner";
import ErrorBoundary from "../components/ErrorBoundary";
import {
  Camera,
  Upload,
  FileImage,
  Truck,
  MessageSquare,
  Search,
  ChevronRight,
  ChevronLeft,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Home,
  User,
  Users,
  LogOut,
  Phone,
  Mail,
  Building2,
  Calendar,
  FileText,
  X,
  Filter,
  Send,
  Package,
  MapPin,
  ClipboardCheck,
  Image,
  ExternalLink,
  Mic,
  MicOff,
  Play,
  Pause,
  Square,
  Trash2,
  Download,
  Menu,
  FolderOpen,
  Ship,
  ListChecks,
  Plus,
  Edit3,
  RefreshCw,
  TrendingUp,
  MessageCircle,
  Container as ContainerIcon,
  PackageOpen,
  StickyNote,
  ToggleLeft,
  ToggleRight,
  Leaf,
  Sparkles,
  Star,
} from "lucide-react";


// Top Header Bar with Burger Menu for Staff
function MobileCODTracker({ pf, token, onDone }) {
  const cod = pf.cod || {};
  const [status, setStatus] = useState(cod.status || pf.cod_status || "none");
  const [dest, setDest] = useState(cod.new_destination || "");
  const [saving, setSaving] = useState(false);
  const badge = { pending: "bg-amber-100 text-amber-700", approved: "bg-green-100 text-green-700", denied: "bg-red-100 text-red-700" };
  const save = async (s) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/position-folders/${pf.id}/cod-status`, { status: s, new_destination: dest, notify_customer: false }, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(s); toast.success(`COD marked ${s}`); onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update COD"); } finally { setSaving(false); }
  };
  return (
    <div className="bg-white rounded-xl p-4 border shadow-sm" data-testid="mobile-cod-tracker">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-sm">Change of Destination</h3>
        {status && status !== "none" && <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${badge[status] || ""}`}>{status}</span>}
      </div>
      <input value={dest} onChange={(e) => setDest(e.target.value)} placeholder="New destination (POD)" className="w-full px-3 py-2 text-sm rounded border border-slate-200 mb-2" data-testid="mobile-cod-dest" />
      <div className="grid grid-cols-3 gap-2">
        {["pending", "approved", "denied"].map((s) => (
          <button key={s} disabled={saving} onClick={() => save(s)} data-testid={`mobile-cod-${s}`}
            className={`text-xs py-2 rounded border capitalize ${status === s ? "bg-slate-800 text-white" : "bg-white text-slate-700"}`}>{s}</button>
        ))}
      </div>
    </div>
  );
}

function MobileSpotButton({ pf, token }) {
  const [open, setOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const openModal = async () => {
    setResult(null); setSelected([]); setOpen(true);
    try {
      const from = pf.origin_port || pf.pol || "Europe";
      const to = pf.destination_port || pf.pod || "Europe";
      const r = await axios.get(`${API_URL}/trucking-companies/match?from_location=${encodeURIComponent(from)}&to_location=${encodeURIComponent(to)}`, { headers: { Authorization: `Bearer ${token}` } });
      setCompanies((r.data.companies || []).filter((c) => c.email));
    } catch { setCompanies([]); }
  };
  const send = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API_URL}/spot-requests/from-pf/${pf.id}`, {
        from_location: pf.origin_port || pf.pol || "", to_location: pf.destination_port || pf.pod || "",
        service_type: pf.service_type || "FCL", description: pf.description || "", company_emails: selected,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(r.data); toast.success(`Sent to ${r.data.emails_sent} companies`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); } finally { setSending(false); }
  };
  return (
    <>
      <button onClick={openModal} data-testid="mobile-spot-btn" className="w-full bg-emerald-600 text-white rounded-xl py-2.5 text-sm font-medium flex items-center justify-center gap-2">
        <Send className="w-4 h-4" /> Spot Rate Request
      </button>
      {open && (
        <div className="fixed inset-0 z-[120] bg-black/50 flex items-end" onClick={() => setOpen(false)}>
          <div className="bg-white w-full rounded-t-2xl p-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mobile-spot-modal">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Spot Rate Request</h3>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            {result ? (
              <div className="space-y-3">
                <p className="text-sm text-emerald-700">Sent to {result.emails_sent} companies. Online link:</p>
                <input readOnly value={result.submit_link} className="w-full text-xs border rounded px-2 py-2 bg-slate-50" data-testid="mobile-spot-link" />
                <button onClick={() => setOpen(false)} className="w-full bg-slate-800 text-white rounded-lg py-2 text-sm">Done</button>
              </div>
            ) : (
              <>
                <p className="text-xs text-slate-500 mb-2">{pf.origin_port || pf.pol || "?"} → {pf.destination_port || pf.pod || "?"} · {pf.service_type || "FCL"}</p>
                <div className="border rounded-lg divide-y max-h-64 overflow-y-auto mb-3">
                  {companies.length === 0 && <p className="p-3 text-xs text-slate-400">No companies with emails found.</p>}
                  {companies.map((c) => (
                    <label key={c.email} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <input type="checkbox" checked={selected.includes(c.email)} onChange={() => setSelected((p) => p.includes(c.email) ? p.filter((e) => e !== c.email) : [...p, c.email])} />
                      <span className="flex-1 truncate">{c.name} <span className="text-xs text-slate-400">{c.email}</span></span>
                    </label>
                  ))}
                </div>
                <button disabled={sending || selected.length === 0} onClick={send} data-testid="mobile-spot-send" className="w-full bg-emerald-600 text-white rounded-lg py-2.5 text-sm font-medium disabled:opacity-50">
                  {sending ? "Sending…" : `Send Request (${selected.length})`}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function StaffTopBar({ user, activeTab, setActiveTab, approvalsPending = 0 }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(["main"]);

  // Grouped menu items
  const menuGroups = [
    {
      id: "main",
      label: "Main",
      items: [
        { id: "home", label: "Home", icon: Home },
        { id: "pfs", label: "Position Folders", icon: FolderOpen },
        { id: "leave", label: "Leave Calendar", icon: Calendar },
      ]
    },
    {
      id: "operations",
      label: "Operations",
      items: [
        { id: "inspect", label: "Inspections", icon: ClipboardCheck },
        { id: "pod", label: "Upload POD", icon: FileImage },
        { id: "transport", label: "Transport Orders", icon: Truck },
      ]
    },
    {
      id: "communication",
      label: "Communication",
      items: [
        { id: "crm", label: "CRM Notes", icon: Building2 },
        { id: "voice", label: "Voice Notes", icon: Mic },
        { id: "notes", label: "Sticky Notes", icon: StickyNote },
        { id: "messages", label: "Messages", icon: MessageSquare },
      ]
    },
    {
      id: "reports",
      label: "Reports",
      items: [
        { id: "trends", label: "Weekly Trends", icon: TrendingUp },
      ]
    },
    {
      id: "account",
      label: "Account",
      items: [
        { id: "profile", label: "My Profile", icon: User },
      ]
    }
  ];

  const tabLabels = {
    home: "Staff Home",
    trends: "Weekly Trends",
    leave: "Leave Calendar",
    inspect: "Inspection",
    crm: "CRM Notes",
    voice: "Voice Notes",
    notes: "Sticky Notes",
    profile: "Profile",
    pod: "Upload POD",
    messages: "Messages",
    transport: "Transport Orders",
    pfs: "Position Folders",
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => 
      prev.includes(groupId)
        ? prev.filter(g => g !== groupId)
        : [...prev, groupId]
    );
  };

  return (
    <>
      <header className="sticky top-0 z-40 bg-slate-800 text-white px-4 py-3 flex items-center justify-between shadow-md" data-testid="staff-top-bar">
        <button 
          onClick={() => setMenuOpen(true)}
          className="relative p-1.5 -ml-1.5 hover:bg-slate-700 rounded-lg transition-colors"
          data-testid="staff-burger-menu"
        >
          <Menu className="w-6 h-6" />
          {approvalsPending > 0 && (
            <span
              className="absolute top-0.5 right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-slate-800 animate-pulse"
              title={`${approvalsPending} registration(s) awaiting approval`}
              data-testid="staff-approvals-dot"
            />
          )}
        </button>
        <h1 className="font-semibold text-lg">{tabLabels[activeTab] || "Staff App"}</h1>
        <div className="w-8" />
      </header>

      {/* Burger Menu Overlay - uses absolute positioning within the mobile app container */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 z-[100]"
              onClick={() => setMenuOpen(false)}
            />
            {/* Side Menu */}
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="absolute left-0 top-0 bottom-0 w-[85%] max-w-[280px] bg-white z-[101] shadow-xl flex flex-col"
              data-testid="staff-side-menu"
            >
              {/* Menu Header */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-5 text-white">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-300">Staff Mobile</span>
                  <button 
                    onClick={() => setMenuOpen(false)}
                    className="p-1 hover:bg-slate-700 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-semibold">{user?.full_name || "Staff"}</p>
                    <p className="text-sm text-slate-300">{user?.email || "staff@europacific.com"}</p>
                  </div>
                </div>
              </div>

              {/* Menu Items - Collapsible Groups */}
              <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                {menuGroups.map((group) => (
                  <div key={group.id} className="mb-2">
                    {/* Group Header */}
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-50 rounded-lg transition-colors"
                      data-testid={`staff-menu-group-${group.id}`}
                    >
                      <span>{group.label}</span>
                      <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${expandedGroups.includes(group.id) ? "rotate-90" : ""}`} />
                    </button>
                    
                    {/* Group Items */}
                    <AnimatePresence initial={false}>
                      {expandedGroups.includes(group.id) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="pl-2 space-y-1 mt-1">
                            {group.items.map((item) => (
                              <button
                                key={item.id}
                                onClick={() => {
                                  setActiveTab(item.id);
                                  setMenuOpen(false);
                                }}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                                  activeTab === item.id
                                    ? "bg-slate-100 text-slate-800"
                                    : "text-slate-700 hover:bg-slate-50"
                                }`}
                                data-testid={`staff-menu-item-${item.id}`}
                              >
                                <item.icon className="w-5 h-5" />
                                <span className="font-medium">{item.label}</span>
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </nav>

              {/* Footer */}
              <div className="p-4 border-t bg-slate-50">
                <p className="text-xs text-slate-400 text-center">
                  Europacific Staff App
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

// Bottom Navigation for Staff
function StaffNavBar({ activeTab, setActiveTab, badges = {} }) {
  const tabs = [
    { id: "home", label: "Home", icon: Home },
    { id: "pfs", label: "PFs", icon: FolderOpen },
    { id: "transport", label: "TOs", icon: Truck },
    { id: "messages", label: "Inbox", icon: MessageSquare },
    { id: "pod", label: "POD", icon: Camera },
    { id: "profile", label: "Me", icon: User },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50 pb-safe" data-testid="staff-nav">
      <div className="flex items-center justify-around py-2">
        {tabs.map((tab) => {
          const badge = badges[tab.id];
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
                activeTab === tab.id ? "text-blue-600" : "text-slate-500"
              }`}
              data-testid={`staff-nav-${tab.id}`}
            >
              <div className="relative">
                <tab.icon className={`w-6 h-6 ${activeTab === tab.id ? "stroke-2" : ""}`} />
                {badge > 0 && (
                  <span
                    className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                    data-testid={`staff-nav-badge-${tab.id}`}
                  >
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </div>
              <span className="text-xs mt-1 font-medium">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// Quick Stat Card - Compact version
function StatCard({ icon: Icon, value, label, color = "blue" }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    orange: "bg-orange-50 text-orange-600",
    green: "bg-green-50 text-green-600",
    red: "bg-red-50 text-red-600",
  };

  return (
    <div className={`p-2.5 rounded-lg ${colors[color]}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <p className="text-lg font-bold">{value}</p>
      </div>
      <p className="text-xs opacity-70 mt-0.5">{label}</p>
    </div>
  );
}

// Home Tab for Staff
function StaffHomeTab({ user, stats, setActiveTab }) {
  return (
    <div className="space-y-6 pb-24">
      {/* Welcome */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-5 text-white">
        <p className="text-slate-300 text-sm">Staff Mobile</p>
        <h1 className="text-xl font-bold mt-1">{user?.full_name}</h1>
        <p className="text-sm text-slate-400 mt-1">{user?.email}</p>
      </div>

      {/* Quick Stats - Compact 4-column grid */}
      <div className="grid grid-cols-4 gap-2">
        <button onClick={() => setActiveTab("messages")} className="text-left" data-testid="home-tickets-tile">
          <StatCard icon={MessageSquare} value={stats.waTicketsPending} label="Tickets" color={stats.waTicketsPending > 0 ? "red" : "blue"} />
        </button>
        <button onClick={() => setActiveTab("pod")} className="text-left">
          <StatCard icon={Camera} value={stats.podPending} label="PODs" color="orange" />
        </button>
        <StatCard icon={ClipboardCheck} value={stats.checksScheduled} label="Inspect" color="red" />
        <StatCard icon={CheckCircle} value={stats.completed} label="Done" color="green" />
      </div>

      {/* WhatsApp Tickets banner — appears only when there are pending */}
      {stats.waTicketsPending > 0 && (
        <button
          onClick={() => setActiveTab("messages")}
          className="w-full bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-left active:bg-emerald-100"
          data-testid="home-tickets-banner"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white">
                <MessageSquare className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-emerald-900">{stats.waTicketsPending} pending WhatsApp ticket{stats.waTicketsPending === 1 ? "" : "s"}</p>
                <p className="text-xs text-emerald-700">Customers waiting for a reply</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-emerald-700" />
          </div>
        </button>
      )}

      {/* Upcoming Inspections */}
      {stats.upcomingChecks?.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            Upcoming Inspections
          </h2>
          <div className="space-y-2">
            {stats.upcomingChecks.slice(0, 3).map((check) => (
              <div
                key={check.id}
                className="bg-red-50 border border-red-200 rounded-xl p-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-red-900">{check.pf_number}</p>
                    <p className="text-sm text-red-700">
                      {new Date(check.scheduled_date).toLocaleString('en-GB', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-300 text-red-700"
                    onClick={() => {
                      setActiveTab("inspect");
                      // Pass the check to inspection tab
                    }}
                  >
                    Start
                  </Button>
                </div>
                {check.location && (
                  <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {check.location}
                  </p>
                )}
                {check.customs_officer_name && (
                  <p className="text-xs text-red-600 mt-1">
                    Officer: {check.customs_officer_name} {check.customs_officer_phone ? `(${check.customs_officer_phone})` : ''}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Quick Actions</h2>
        <div className="space-y-2">
          <button
            onClick={() => setActiveTab("pfs")}
            className="w-full bg-white rounded-xl border p-4 flex items-center gap-4 active:scale-98 transition-transform"
            data-testid="quick-pfs"
          >
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              <FolderOpen className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1 text-left">
              <h3 className="font-semibold">Position Folders</h3>
              <p className="text-sm text-slate-500">View PFs, tasks & updates</p>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>

          <button
            onClick={() => setActiveTab("inspect")}
            className="w-full bg-white rounded-xl border p-4 flex items-center gap-4 active:scale-98 transition-transform"
            data-testid="quick-inspect"
          >
            <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
              <ClipboardCheck className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1 text-left">
              <h3 className="font-semibold">Inspections</h3>
              <p className="text-sm text-slate-500">{stats.checksScheduled} scheduled</p>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>

          <button
            onClick={() => setActiveTab("pod")}
            className="w-full bg-white rounded-xl border p-4 flex items-center gap-4 active:scale-98 transition-transform"
            data-testid="quick-upload-pod"
          >
            <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
              <Camera className="w-6 h-6 text-orange-600" />
            </div>
            <div className="flex-1 text-left">
              <h3 className="font-semibold">Upload POD</h3>
              <p className="text-sm text-slate-500">{stats.podPending} orders need POD</p>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>

          <button
            onClick={() => setActiveTab("crm")}
            className="w-full bg-white rounded-xl border p-4 flex items-center gap-4 active:scale-98 transition-transform"
            data-testid="quick-add-note"
          >
            <div className="h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center">
              <FileText className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="flex-1 text-left">
              <h3 className="font-semibold">Add CRM Note</h3>
              <p className="text-sm text-slate-500">Log call or meeting</p>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Position Folders Tab for Mobile - with Status Updates, Tasks, and ETAs
function PositionFoldersTab({ token, onJumpToTo, pendingOpenPf, clearPendingOpenPf }) {
  const [loading, setLoading] = useState(true);
  const [pfs, setPfs] = useState([]);
  const [selectedPf, setSelectedPf] = useState(null);
  const [search, setSearch] = useState("");
  // 2026-05-20: server-side pagination + debounced search. Previous
  // implementation loaded 20 PFs and filtered locally — staff couldn't reach
  // older PFs and certain non-string field values could crash the render
  // (white screen).
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const PER_PAGE = 50;
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // 2026-05-17 — Transport messaging (Msg Dispatcher / Msg Driver / Ping
  // Driver GPS) moved from PF detail to Transport Orders detail tab.
  // 2026-05-16: extra actions for offsite-work parity
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentAudience, setCommentAudience] = useState("customer");
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [newTaskLabel, setNewTaskLabel] = useState("");
  const [togglingWorkflow, setTogglingWorkflow] = useState(false);
  // 2026-05-16: per-PF CO2 footprint
  const [co2, setCo2] = useState(null);
  // 2026-05-17: TOs linked to the currently-open PF (so staff can
  // jump straight to a connected Transport Order from the PF detail).
  const [linkedTos, setLinkedTos] = useState([]);
  const [loadingTos, setLoadingTos] = useState(false);

  // 2026-05-22 — Tracked Customer Email composer (parity with desktop
  // PF "Email" tab). Uses the existing /position-folders/{id}/send-email
  // endpoint so sends are audit-logged on the PF (mailto: in the quick
  // bar opens the native mail app and bypasses that audit trail).
  const [showPfEmailModal, setShowPfEmailModal] = useState(false);
  const [pfEmailSubject, setPfEmailSubject] = useState("");
  const [pfEmailBody, setPfEmailBody] = useState("");
  const [pfEmailTo, setPfEmailTo] = useState("");
  const [pfEmailCc, setPfEmailCc] = useState("");
  const [pfEmailSending, setPfEmailSending] = useState(false);
  // Prefill To (customer + notify) and Cc (notify) when the email modal opens
  useEffect(() => {
    if (showPfEmailModal && selectedPf) {
      setPfEmailTo([selectedPf.customer_email, ...(selectedPf.notification_emails || [])].filter(Boolean).join(", "));
      setPfEmailCc((selectedPf.notification_emails || []).filter(Boolean).join(", "));
    }
  }, [showPfEmailModal, selectedPf]);

  // 2026-05-27 — Master/sub PF browsing on mobile. When the user taps a
  // master PF we fetch its sub-orders so they can be opened individually.
  const [subOrders, setSubOrders] = useState([]);
  const [loadingSubOrders, setLoadingSubOrders] = useState(false);

  const PF_QUICK_TEMPLATES = [
    {
      id: "status", label: "Status update",
      subject: (pf) => `Status update — ${pf?.pf_number || ""}`,
      body: (pf) => `Dear ${pf?.customer_name || "Customer"},\n\nQuick update on your shipment ${pf?.pf_number || ""}:\n\n- Current status: \n- Next milestone: \n- ETA: \n\nBest regards,\nEuropacific Ops`,
    },
    {
      id: "docs", label: "Request documents",
      subject: (pf) => `Documents needed — ${pf?.pf_number || ""}`,
      body: (pf) => `Dear ${pf?.customer_name || "Customer"},\n\nTo move forward with ${pf?.pf_number || ""} we still need:\n\n- Commercial invoice\n- Packing list\n- (please add anything else)\n\nThanks in advance,\nEuropacific Ops`,
    },
    {
      id: "delivery", label: "Delivery scheduled",
      subject: (pf) => `Delivery scheduled — ${pf?.pf_number || ""}`,
      body: (pf) => `Dear ${pf?.customer_name || "Customer"},\n\nWe have scheduled the delivery for your shipment ${pf?.pf_number || ""}.\n\n- Date: \n- Time window: \n- Driver mobile: \n\nPlease confirm someone will be available to receive the goods.\n\nBest regards,\nEuropacific Ops`,
    },
    {
      id: "delivered", label: "Delivered — POD attached",
      subject: (pf) => `POD — ${pf?.pf_number || ""}`,
      body: (pf) => `Dear ${pf?.customer_name || "Customer"},\n\nYour shipment ${pf?.pf_number || ""} has been delivered. POD is attached.\n\nThank you for your business.\n\nBest regards,\nEuropacific Ops`,
    },
    {
      id: "delay", label: "Delay notice",
      subject: (pf) => `Delay notice — ${pf?.pf_number || ""}`,
      body: (pf) => `Dear ${pf?.customer_name || "Customer"},\n\nWe wanted to let you know that ${pf?.pf_number || ""} is currently experiencing a delay.\n\n- Reason: \n- Revised ETA: \n\nWe apologise for the inconvenience and will keep you posted.\n\nBest regards,\nEuropacific Ops`,
    },
  ];

  const applyPfTemplate = (tid) => {
    const tpl = PF_QUICK_TEMPLATES.find(t => t.id === tid);
    if (!tpl) return;
    setPfEmailSubject(tpl.subject(selectedPf));
    setPfEmailBody(tpl.body(selectedPf));
  };

  const sendPfTrackedEmail = async () => {
    if (!selectedPf || !pfEmailBody.trim()) return;
    const toList = pfEmailTo.split(",").map((e) => e.trim()).filter(Boolean);
    if (toList.length === 0) {
      toast.error("Please enter at least one recipient in 'To'");
      return;
    }
    setPfEmailSending(true);
    try {
      const fd = new FormData();
      fd.append("subject", pfEmailSubject || `Re: ${selectedPf.pf_number || ""}`);
      fd.append("body", pfEmailBody);
      fd.append("to_emails", JSON.stringify(toList));
      fd.append("cc_emails", JSON.stringify(pfEmailCc.split(",").map((e) => e.trim()).filter(Boolean)));
      await axios.post(
        `${API_URL}/position-folders/${selectedPf.id}/send-email`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } },
      );
      toast.success(`Email sent to ${toList.length} recipient(s)`);
      setPfEmailSubject("");
      setPfEmailBody("");
      setShowPfEmailModal(false);
      fetchPfDetails(selectedPf.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally {
      setPfEmailSending(false);
    }
  };

  useEffect(() => {
    fetchPFs({ page: 1, q: search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2026-05-20: debounced server-side search. Re-runs whenever the user
  // types — backend handles regex match across pf_number / customer / MBL /
  // HBL / container / etc. Replaces the previous local `.filter()` which
  // could only see the first 20 PFs and crashed on non-string fields.
  useEffect(() => {
    const t = setTimeout(() => {
      fetchPFs({ page: 1, q: search });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // 2026-05-17 — Reverse deep-link from TO: open the requested PF.
  useEffect(() => {
    if (!pendingOpenPf) return;
    const ref = typeof pendingOpenPf === "string"
      ? pendingOpenPf
      : (pendingOpenPf.id || pendingOpenPf.pf_number);
    if (ref) fetchPfDetails(ref);
    if (clearPendingOpenPf) clearPendingOpenPf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpenPf]);

  const fetchPFs = async ({ page: targetPage = 1, q = "" } = {}) => {
    const isFirstPage = targetPage === 1;
    if (isFirstPage) setLoading(true);
    else setLoadingMore(true);
    try {
      // When searching, drop the status filter so staff can find any PF
      // (including closed / transferred). Without search, default to open
      // PFs only.
      const params = new URLSearchParams({
        page: String(targetPage),
        per_page: String(PER_PAGE),
        sort_by: "pf_number",
        sort_order: "desc",
      });
      if (q && q.trim().length >= 2) {
        params.set("search", q.trim());
      } else {
        params.set("status", "Odprto");
      }
      const response = await axios.get(`${API_URL}/position-folders?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data || {};
      const incoming = Array.isArray(data.folders) ? data.folders : [];
      setPfs(isFirstPage ? incoming : (prev) => [...prev, ...incoming]);
      setPage(targetPage);
      setTotalCount(Number(data.total) || 0);
      setHasMore(targetPage < Number(data.total_pages || 0));
    } catch (error) {
      console.error("Error fetching PFs:", error);
      toast.error("Failed to load Position Folders");
      if (isFirstPage) setPfs([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchPfDetails = async (pfId) => {
    try {
      const response = await axios.get(`${API_URL}/position-folders/${pfId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedPf(response.data);
      // 2026-05-27 — If this PF is a master, fetch its sub-orders so the
      // detail view can render a tappable Sub-orders section.
      setSubOrders([]);
      if (response.data?.is_master) {
        setLoadingSubOrders(true);
        try {
          const subs = await axios.get(
            `${API_URL}/position-folders/${pfId}/sub-orders`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          setSubOrders(subs.data?.sub_orders || []);
        } catch (e) {
          console.error("Failed to load sub-orders", e);
          setSubOrders([]);
        } finally {
          setLoadingSubOrders(false);
        }
      }
      // 2026-05-16: load CO2 for this PF too
      try {
        const co2r = await axios.get(`${API_URL}/position-folders/${pfId}/co2`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCo2(co2r.data);
      } catch { setCo2(null); }
      // 2026-05-17: load connected Transport Orders for the jump-button.
      const pfNumber = response.data?.pf_number;
      if (pfNumber) {
        setLoadingTos(true);
        try {
          const tor = await axios.get(
            `${API_URL}/transport-orders?search=${encodeURIComponent(pfNumber)}&per_page=20`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          const all = tor.data?.orders || tor.data || [];
          // Server search is fuzzy — keep only exact pf_number matches.
          setLinkedTos(all.filter((t) => t.pf_number === pfNumber));
        } catch { setLinkedTos([]); }
        finally { setLoadingTos(false); }
      } else {
        setLinkedTos([]);
      }
    } catch (error) {
      toast.error("Failed to load PF details");
    }
  };

  const submitStatusUpdate = async () => {
    if (!statusUpdate.trim() || !selectedPf) return;
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/position-folders/${selectedPf.id}/status-update`,
        { 
          status: statusUpdate,
          source: "mobile_app",
          note: `Manual update via mobile app`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Status update added!");
      setStatusUpdate("");
      setShowStatusModal(false);
      fetchPfDetails(selectedPf.id);
    } catch (error) {
      toast.error("Failed to add status update");
    } finally {
      setSubmitting(false);
    }
  };

  // 2026-05-16 — quick add comment (visible on the desktop PF too)
  const submitComment = async () => {
    if (!commentText.trim() || !selectedPf) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/position-folders/${selectedPf.id}/comments`,
        { text: commentText.trim(), audience: commentAudience, source: "myeuropacific" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Comment added!");
      setCommentText("");
      setShowCommentModal(false);
      fetchPfDetails(selectedPf.id);
    } catch (error) {
      toast.error("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  // 2026-05-16 — quick add task from the field
  const submitAddTask = async () => {
    if (!newTaskLabel.trim() || !selectedPf) return;
    setSubmitting(true);
    try {
      const existing = (selectedPf.tasks || []).map((t) => ({ ...t }));
      const nextTasks = [
        ...existing,
        { label: newTaskLabel.trim(), completed: false, source: "mobile_app" },
      ];
      await axios.put(
        `${API_URL}/position-folders/${selectedPf.id}/tasks`,
        { tasks: nextTasks },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Task added!");
      setNewTaskLabel("");
      setShowAddTaskModal(false);
      fetchPfDetails(selectedPf.id);
    } catch (error) {
      toast.error("Failed to add task");
    } finally {
      setSubmitting(false);
    }
  };

  // 2026-05-16: toggle tracking finished (stop/start) from the field
  const [togglingTracking, setTogglingTracking] = useState(false);
  const toggleTrackingFinished = async () => {
    if (!selectedPf || togglingTracking) return;
    const next = !selectedPf.tracking_finished;
    setTogglingTracking(true);
    try {
      await axios.put(
        `${API_URL}/position-folders/${selectedPf.id}/tracking-finished`,
        { finished: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(next ? "Tracking stopped" : "Tracking restarted");
      fetchPfDetails(selectedPf.id);
    } catch (error) {
      toast.error("Failed to update tracking");
    } finally {
      setTogglingTracking(false);
    }
  };

  // 2026-05-16: toggle auto/pending workflow mode from the road
  const toggleWorkflowMode = async () => {
    if (!selectedPf || togglingWorkflow) return;
    const next = (selectedPf.workflow_mode === "auto") ? "pending" : "auto";
    setTogglingWorkflow(true);
    try {
      await axios.put(
        `${API_URL}/position-folders/${selectedPf.id}/workflow-mode`,
        { mode: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Workflow set to ${next.toUpperCase()}`);
      fetchPfDetails(selectedPf.id);
    } catch (error) {
      toast.error("Failed to update workflow mode");
    } finally {
      setTogglingWorkflow(false);
    }
  };

  const toggleTask = async (taskId) => {
    if (!selectedPf) return;
    try {
      await axios.put(
        `${API_URL}/position-folders/${selectedPf.id}/tasks/${taskId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPfDetails(selectedPf.id);
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  // 2026-05-17 — `sendTransportMessage` and `pingDriverLocation` moved
  // to the Transport Orders detail view (TransportOrdersTab).

  // 2026-05-20: server now does the filtering — no local `.filter()` (the
  // previous version could throw `toLowerCase is not a function` if a PF
  // had a non-string field, causing the entire React tree to white-screen).
  // We still defensively coerce values during render below.
  const safeLower = (v) => (typeof v === "string" ? v.toLowerCase() : "");
  const visiblePfs = Array.isArray(pfs) ? pfs : [];

  const fetchRefresh = () => fetchPFs({ page: 1, q: search });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "-";
      return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    } catch {
      return "-";
    }
  };

  // PF List View
  if (!selectedPf) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-blue-600" />
            Position Folders
          </h2>
          <Button size="sm" variant="ghost" onClick={fetchRefresh} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="pfs-search-input"
            placeholder="Search PF, customer, MBL..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : visiblePfs.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <FolderOpen className="w-12 h-12 mx-auto mb-2 text-slate-300" />
            <p>{search ? `No PFs match "${search}"` : "No open Position Folders"}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {visiblePfs.map((pf) => (
              <div
                key={pf.id || pf.pf_number}
                onClick={() => fetchPfDetails(pf.id)}
                data-testid={`pf-card-${pf.pf_number}`}
                className="bg-white rounded-xl p-3 border shadow-sm active:bg-slate-50 cursor-pointer"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="font-semibold text-blue-600">{String(pf.pf_number || "")}</p>
                      {pf.status && (
                        <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                          pf.status === "closed" ? "bg-slate-200 text-slate-600"
                          : pf.status === "completed" || pf.status === "delivered" ? "bg-green-100 text-green-700"
                          : "bg-blue-50 text-blue-700"
                        }`}>{String(pf.status)}</span>
                      )}
                      {pf.workflow_mode === "auto" && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                          AUTO
                        </span>
                      )}
                      {pf.is_master && (
                        <span
                          className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 inline-flex items-center gap-0.5"
                          data-testid={`pf-master-badge-${pf.pf_number}`}
                          title="This is a master PF — tap to view sub-orders"
                        >
                          Master{typeof pf.sub_orders_count === "number" ? ` (${pf.sub_orders_count} sub)` : ""}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {String(pf.customer_name || pf.customer || pf.customer_ref || "— customer not set —")}
                    </p>
                    {pf.assignee && (
                      <p className="text-xs text-slate-500 truncate">👤 {String(pf.assignee)}</p>
                    )}
                    {pf.description && (
                      <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{String(pf.description)}</p>
                    )}
                    <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500 flex-wrap">
                      {pf.service_type && (
                        <span className="px-1.5 py-0.5 bg-slate-100 rounded uppercase font-medium">
                          {pf.service_type}
                        </span>
                      )}
                      {pf.direction && (
                        <span className="capitalize text-slate-400">{pf.direction}</span>
                      )}
                      {pf.mbl_number && (
                        <span className="truncate max-w-[120px]" title={String(pf.mbl_number)}>{String(pf.mbl_number)}</span>
                      )}
                      {pf.document_date && (
                        <span className="text-slate-400">· {formatDate(pf.document_date)}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    {pf.eta && (
                      <p className="text-xs">
                        <span className="text-slate-500">ETA:</span>{" "}
                        <span className="font-medium text-emerald-600">{formatDate(pf.eta)}</span>
                      </p>
                    )}
                    {Array.isArray(pf.tasks) && pf.tasks.length > 0 && (
                      <p className="text-xs text-slate-500 mt-1">
                        {pf.tasks.filter(t => t && t.completed).length}/{pf.tasks.length} tasks
                      </p>
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-400 mt-1 ml-auto" />
                  </div>
                </div>
              </div>
            ))}

            {/* Load More */}
            {hasMore && (
              <div className="pt-2 pb-1 flex justify-center">
                <Button
                  data-testid="pfs-load-more-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => fetchPFs({ page: page + 1, q: search })}
                  disabled={loadingMore}
                  className="min-w-[180px]"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading…
                    </>
                  ) : (
                    `Load more (${visiblePfs.length} / ${totalCount})`
                  )}
                </Button>
              </div>
            )}
            {!hasMore && visiblePfs.length > 0 && totalCount > 0 && (
              <p className="text-center text-[11px] text-slate-400 pt-1">
                {visiblePfs.length} of {totalCount} PF{totalCount !== 1 ? "s" : ""}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  // PF Detail View
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => setSelectedPf(null)}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h2 className="font-semibold text-blue-600">{selectedPf.pf_number}</h2>
          <p className="text-sm text-slate-600">{selectedPf.customer_name || selectedPf.customer || "—"}</p>
        </div>
        <Button size="sm" onClick={() => setShowStatusModal(true)}>
          <Plus className="w-4 h-4 mr-1" />
          Status
        </Button>
      </div>

      {/* Quick Info */}
      <div className="bg-white rounded-xl p-4 border shadow-sm">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs text-slate-500">Service</p>
            <p className="font-medium">{selectedPf.service_type || "-"}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Direction</p>
            <p className="font-medium capitalize">{selectedPf.direction || "-"}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">ETA</p>
            <p className="font-medium text-emerald-600">{formatDate(selectedPf.eta)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">ETD</p>
            <p className="font-medium">{formatDate(selectedPf.etd)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">POL (Origin)</p>
            <p className="font-medium text-xs">{selectedPf.origin_port || selectedPf.pol || "-"}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">POD (Destination)</p>
            <p className="font-medium text-xs">{selectedPf.destination_port || selectedPf.pod || "-"}</p>
          </div>
          {selectedPf.booking_number && (
            <div>
              <p className="text-xs text-slate-500">Booking</p>
              <p className="font-medium text-xs">{selectedPf.booking_number}</p>
            </div>
          )}
          {(selectedPf.shipping_line || selectedPf.carrier) && (
            <div>
              <p className="text-xs text-slate-500">Carrier</p>
              <p className="font-medium text-xs">{selectedPf.shipping_line || selectedPf.carrier}</p>
            </div>
          )}
          {selectedPf.assignee && (
            <div>
              <p className="text-xs text-slate-500">Assignee</p>
              <p className="font-medium text-xs">{selectedPf.assignee}</p>
            </div>
          )}
          {selectedPf.document_date && (
            <div>
              <p className="text-xs text-slate-500">Doc Date</p>
              <p className="font-medium text-xs">{formatDate(selectedPf.document_date)}</p>
            </div>
          )}
          {selectedPf.mbl_number && (
            <div className="col-span-2">
              <p className="text-xs text-slate-500">MBL</p>
              <p className="font-medium text-xs">{selectedPf.mbl_number}</p>
            </div>
          )}
          {selectedPf.hbl_number && (
            <div className="col-span-2">
              <p className="text-xs text-slate-500">HBL</p>
              <p className="font-medium text-xs">{selectedPf.hbl_number}</p>
            </div>
          )}
          {selectedPf.vessel_name && (
            <div className="col-span-2">
              <p className="text-xs text-slate-500">Vessel</p>
              <p className="font-medium">{selectedPf.vessel_name} {selectedPf.voyage && `/ ${selectedPf.voyage}`}</p>
            </div>
          )}
          {selectedPf.description && (
            <div className="col-span-2">
              <p className="text-xs text-slate-500">Description</p>
              <p className="text-xs text-slate-700 line-clamp-3">{selectedPf.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Shipment change tools: COD + Spot Rate Request */}
      <MobileCODTracker pf={selectedPf} token={token} onDone={() => fetchPfDetails(selectedPf.id)} />
      <MobileSpotButton pf={selectedPf} token={token} />

      {/* 2026-05-27 — Sub-orders section. Shown only for master PFs so staff
          can tap into each sub-PF directly from the PWA (parity with the
          desktop "Master (N sub)" expandable rows). */}
      {selectedPf.is_master && (
        <div className="bg-white rounded-xl p-4 border shadow-sm" data-testid="pf-sub-orders-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <FolderOpen className="w-4 h-4 text-purple-600" />
              Sub-orders {subOrders.length > 0 && <span className="text-xs text-slate-500 font-normal">({subOrders.length})</span>}
            </h3>
            {loadingSubOrders && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          </div>
          {!loadingSubOrders && subOrders.length === 0 ? (
            <p className="text-xs text-slate-500">No sub-orders yet for this master PF.</p>
          ) : (
            <div className="space-y-2">
              {subOrders.map((sub) => (
                <button
                  key={sub.id || sub.pf_number}
                  type="button"
                  onClick={() => fetchPfDetails(sub.id)}
                  data-testid={`pf-sub-order-${sub.pf_number}`}
                  className="w-full text-left bg-amber-50/60 hover:bg-amber-100 active:bg-amber-100 border border-amber-200 rounded-lg p-2.5 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-blue-600 text-sm">{String(sub.pf_number || "")}</span>
                        {sub.hbl_number && (
                          <span className="text-[10px] bg-white border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-mono">
                            HBL: {String(sub.hbl_number)}
                          </span>
                        )}
                        {sub.status && (
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                            {String(sub.status)}
                          </span>
                        )}
                      </div>
                      {sub.customer_name && (
                        <p className="text-xs text-slate-700 truncate mt-0.5">{String(sub.customer_name)}</p>
                      )}
                      {(sub.weight_kg || sub.volume_cbm) && (
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {sub.weight_kg ? `${sub.weight_kg} kg` : ""}
                          {sub.weight_kg && sub.volume_cbm ? "  ·  " : ""}
                          {sub.volume_cbm ? `${sub.volume_cbm} CBM` : ""}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Workflow status checkboxes (read-only display from desktop view) */}
      <div className="bg-white rounded-xl p-4 border shadow-sm" data-testid="pf-workflow-status">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-blue-600" />
            Workflow Status
          </h3>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            onClick={toggleWorkflowMode}
            disabled={togglingWorkflow}
            data-testid="pf-toggle-workflow-mode"
          >
            {selectedPf.workflow_mode === "auto" ? (
              <><ToggleRight className="w-4 h-4 mr-1 text-green-600" /> AUTO</>
            ) : (
              <><ToggleLeft className="w-4 h-4 mr-1 text-amber-600" /> PENDING</>
            )}
          </Button>
        </div>

        {/* Tracking + Close PF quick actions */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <Button
            size="sm"
            variant="outline"
            onClick={toggleTrackingFinished}
            disabled={togglingTracking}
            data-testid="pf-toggle-tracking-btn"
            className={selectedPf.tracking_finished
              ? "border-blue-200 text-blue-700"
              : "border-amber-200 text-amber-700"}
          >
            {togglingTracking ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : selectedPf.tracking_finished ? (
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            ) : (
              <X className="w-3.5 h-3.5 mr-1.5" />
            )}
            {selectedPf.tracking_finished ? "Restart tracking" : "Stop tracking"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              if (!window.confirm(`Close ${selectedPf.pf_number}? This marks it as completed.`)) return;
              try {
                await axios.put(
                  `${API_URL}/position-folders/${selectedPf.id}/tracking-finished`,
                  { finished: true },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                // Bonus: set status to closed via comments/status endpoint
                try {
                  await axios.post(
                    `${API_URL}/position-folders/${selectedPf.id}/status-update`,
                    { status: "closed", note: "Closed from mobile app" },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                } catch { /* status endpoint may not exist on all builds */ }
                toast.success(`${selectedPf.pf_number} closed`);
                fetchPfDetails(selectedPf.id);
              } catch {
                toast.error("Failed to close PF");
              }
            }}
            className="border-slate-300 text-slate-700"
            data-testid="pf-close-btn"
          >
            <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
            Close PF
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-1.5 text-xs">
          {[
            { key: "pf_created", label: "PF Created" },
            { key: "track_shipping", label: "Track Shipping" },
            { key: "arrival_notice_sent", label: "Arrival Notice" },
            { key: "track_port", label: "Track Port" },
            { key: "track_trucking", label: "Track Trucking" },
            { key: "delivered", label: "Delivered" },
            { key: "pod_received", label: "POD Received" },
            { key: "invoiced", label: "Invoiced" },
          ].map((step) => {
            const done = !!selectedPf[step.key];
            return (
              <div key={step.key} className={`flex items-center gap-1.5 p-1.5 rounded ${done ? "bg-green-50 text-green-700" : "bg-slate-50 text-slate-500"}`}>
                <div className={`w-3 h-3 rounded border flex items-center justify-center ${done ? "bg-green-500 border-green-500 text-white" : "border-slate-300"}`}>
                  {done && <CheckCircle className="w-2.5 h-2.5" />}
                </div>
                <span>{step.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Containers & Cargo */}
      {(selectedPf.containers?.length > 0 || selectedPf.cargo_pieces || selectedPf.weight_kg || selectedPf.volume_cbm) && (
        <div className="bg-white rounded-xl p-4 border shadow-sm" data-testid="pf-cargo-block">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <ContainerIcon className="w-4 h-4 text-indigo-600" />
            Containers &amp; Cargo
          </h3>
          {selectedPf.containers?.length > 0 && (
            <div className="space-y-1 mb-2">
              {selectedPf.containers.slice(0, 6).map((c, i) => (
                <div key={i} className="text-xs flex justify-between border-b pb-1">
                  <span className="font-mono">{c.container_number || c.number || "-"}</span>
                  <span className="text-slate-500">{c.container_type || c.type || ""}</span>
                </div>
              ))}
              {selectedPf.containers.length > 6 && (
                <p className="text-xs text-slate-400">+ {selectedPf.containers.length - 6} more</p>
              )}
            </div>
          )}
          <div className="grid grid-cols-3 gap-2 text-xs">
            {selectedPf.cargo_pieces != null && (
              <div><p className="text-slate-500">Pieces</p><p className="font-medium">{selectedPf.cargo_pieces}</p></div>
            )}
            {selectedPf.weight_kg != null && (
              <div><p className="text-slate-500">Weight</p><p className="font-medium">{selectedPf.weight_kg} kg</p></div>
            )}
            {selectedPf.volume_cbm != null && (
              <div><p className="text-slate-500">Volume</p><p className="font-medium">{selectedPf.volume_cbm} cbm</p></div>
            )}
          </div>
        </div>
      )}

      {/* CO2 footprint — 2026-05-16 */}
      <div className="bg-white rounded-xl p-4 border shadow-sm" data-testid="pf-co2-block">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold flex items-center gap-2">
            <Leaf className="w-4 h-4 text-green-600" />
            Carbon footprint
          </h3>
          {co2 && co2.method === "estimated" && (
            <span className="text-[10px] uppercase tracking-wider text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
              GLEC · est.
            </span>
          )}
        </div>
        {!co2 ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : co2.co2_kg == null ? (
          <p className="text-xs text-slate-500">{co2.reason || "Insufficient data."}</p>
        ) : (
          <>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-green-700">{co2.co2_kg.toLocaleString()}</span>
              <span className="text-sm text-slate-500">kg CO₂e</span>
            </div>
            <div className="text-xs text-slate-500 mt-1 grid grid-cols-2 gap-1">
              <span>Mode: <b className="text-slate-700 capitalize">{co2.mode}</b></span>
              <span>Distance: <b className="text-slate-700">{co2.distance_km?.toLocaleString()} km</b></span>
              <span>Weight: <b className="text-slate-700">{co2.weight_tonnes} t</b></span>
              <span>Factor: <b className="text-slate-700">{co2.factor_g_per_tonne_km} g/t·km</b></span>
            </div>
          </>
        )}
      </div>

      {/* 2026-05-16 — AWB tracker (only for AIR shipments) */}
      {String(selectedPf.service_type || "").toUpperCase() === "AIR" &&
        (selectedPf.mbl_number || selectedPf.awb_number || selectedPf.hawb_number) && (
          <AwbTracker
            mawb={selectedPf.mbl_number || selectedPf.awb_number || selectedPf.hawb_number}
            pfNumber={selectedPf.pf_number}
            token={token}
            contactEmail={selectedPf.customer_email || ""}
            contactPhone={selectedPf.customer_phone || ""}
          />
        )}

      {/* Route */}
      <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-xl p-4 border">
        <div className="flex items-center gap-2">
          <div className="flex-1 text-center">
            <p className="text-xs text-slate-500">Origin</p>
            <p className="font-medium text-sm">{selectedPf.origin_port || "-"}</p>
          </div>
          <Ship className="w-5 h-5 text-blue-500" />
          <div className="flex-1 text-center">
            <p className="text-xs text-slate-500">Destination</p>
            <p className="font-medium text-sm">{selectedPf.destination_port || "-"}</p>
          </div>
        </div>
      </div>

      {/* 2026-05-17 — Connected Transport Orders. Jumps to the TOs tab
          and pre-opens the chosen TO so Anja can continue work there. */}
      {(loadingTos || linkedTos.length > 0) && (
        <div
          className="bg-white rounded-xl p-4 border shadow-sm"
          data-testid="pf-linked-tos-card"
        >
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Truck className="w-4 h-4 text-purple-600" />
            Connected Transport Orders
            {linkedTos.length > 0 && (
              <span className="text-xs text-slate-400">({linkedTos.length})</span>
            )}
          </h3>
          {loadingTos ? (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-2">
              {linkedTos.map((to) => (
                <button
                  key={to.id}
                  onClick={() => onJumpToTo && onJumpToTo(to)}
                  className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-purple-50 hover:border-purple-200 text-left transition-colors"
                  data-testid={`pf-jump-to-to-${to.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-800 truncate">
                      {to.order_number}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {to.trucking_company_name || to.trucking_company?.name || "Carrier pending"}
                      {to.unloading_place ? ` → ${to.unloading_place}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-2 shrink-0">
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        (to.workflow_status || "").toLowerCase() === "completed" ||
                        (to.workflow_status || "").toLowerCase() === "pod_received"
                          ? "bg-green-100 text-green-700"
                          : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {(to.workflow_status || "pending").replace(/_/g, " ")}
                    </span>
                    <ChevronRight className="w-4 h-4 text-purple-500" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tasks */}
      <div className="bg-white rounded-xl p-4 border shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-blue-600" />
            Tasks ({selectedPf.tasks?.filter(t => t.completed).length || 0}/{selectedPf.tasks?.length || 0})
          </h3>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            onClick={() => setShowAddTaskModal(true)}
            data-testid="pf-add-task-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>
        {selectedPf.tasks && selectedPf.tasks.length > 0 ? (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {selectedPf.tasks.map((task) => (
              <div
                key={task.id}
                onClick={() => toggleTask(task.id)}
                className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer ${
                  task.completed ? "bg-green-50" : "bg-slate-50"
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  task.completed ? "bg-green-500 border-green-500 text-white" : "border-slate-300"
                }`}>
                  {task.completed && <CheckCircle className="w-3 h-3" />}
                </div>
                <span className={`text-sm flex-1 ${task.completed ? "line-through text-slate-500" : ""}`}>
                  {task.label}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-2">No tasks</p>
        )}
      </div>

      {/* Recent Status Updates */}
      <div className="bg-white rounded-xl p-4 border shadow-sm">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-orange-500" />
          Recent Updates
        </h3>
        {selectedPf.status_updates && selectedPf.status_updates.length > 0 ? (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {selectedPf.status_updates.slice(0, 5).map((update, idx) => (
              <div key={idx} className="text-sm border-l-2 border-blue-200 pl-3 py-1">
                <p className="text-slate-700">{update.status || update.message}</p>
                <p className="text-xs text-slate-400">
                  {new Date(update.timestamp || update.created_at).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-2">No status updates</p>
        )}
      </div>

      {/* Comments (visible to customer / internal) */}
      <div className="bg-white rounded-xl p-4 border shadow-sm" data-testid="pf-comments-block">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <StickyNote className="w-4 h-4 text-amber-600" />
            Comments ({selectedPf.comments?.length || 0})
          </h3>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            onClick={() => setShowCommentModal(true)}
            data-testid="pf-add-comment-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>
        {selectedPf.comments && selectedPf.comments.length > 0 ? (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {selectedPf.comments.slice(0, 5).map((c, idx) => (
              <div key={c.id || idx} className="text-xs border-l-2 border-amber-200 pl-2 py-1">
                <p className="text-slate-700">{c.text}</p>
                <p className="text-[11px] text-slate-400">
                  {c.audience === "master" ? "🔒 internal" : "👤 customer"} •
                  {" "}{c.author_email || "—"} •
                  {" "}{new Date(c.created_at || c.timestamp).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-2">No comments yet</p>
        )}
      </div>

      {/* Communicate with customer / Open on web */}
      <div className="bg-white rounded-xl p-4 border shadow-sm" data-testid="pf-communicate-block">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-cyan-600" />
          Communicate
        </h3>
        <div className="grid grid-cols-3 gap-2">
          {selectedPf.customer_phone ? (
            <a
              href={`tel:${selectedPf.customer_phone}`}
              className="flex flex-col items-center gap-1 p-2 border rounded-lg text-xs hover:bg-slate-50"
              data-testid="pf-call-customer"
            >
              <Phone className="w-4 h-4 text-blue-600" />
              <span>Call</span>
            </a>
          ) : (
            <div className="flex flex-col items-center gap-1 p-2 border rounded-lg text-xs opacity-40">
              <Phone className="w-4 h-4" />
              <span>Call</span>
            </div>
          )}
          {selectedPf.customer_email ? (
            <a
              href={`mailto:${selectedPf.customer_email}?subject=${encodeURIComponent(`Re: ${selectedPf.pf_number}`)}`}
              className="flex flex-col items-center gap-1 p-2 border rounded-lg text-xs hover:bg-slate-50"
              data-testid="pf-email-customer"
            >
              <Mail className="w-4 h-4 text-emerald-600" />
              <span>Email</span>
            </a>
          ) : (
            <div className="flex flex-col items-center gap-1 p-2 border rounded-lg text-xs opacity-40">
              <Mail className="w-4 h-4" />
              <span>Email</span>
            </div>
          )}
          {selectedPf.customer_phone ? (
            <a
              href={`https://wa.me/${(selectedPf.customer_phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Hi, regarding ${selectedPf.pf_number}: `)}`}
              target="_blank"
              rel="noreferrer"
              className="flex flex-col items-center gap-1 p-2 border rounded-lg text-xs hover:bg-slate-50"
              data-testid="pf-whatsapp-customer"
            >
              <MessageSquare className="w-4 h-4 text-green-600" />
              <span>WhatsApp</span>
            </a>
          ) : (
            <div className="flex flex-col items-center gap-1 p-2 border rounded-lg text-xs opacity-40">
              <MessageSquare className="w-4 h-4" />
              <span>WhatsApp</span>
            </div>
          )}
          <button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(`${window.location.origin}/position-folders?pf=${selectedPf.pf_number}`);
                toast.success("PF link copied to clipboard");
              } catch {
                toast.error("Couldn't copy link");
              }
            }}
            className="flex flex-col items-center gap-1 p-2 border rounded-lg text-xs hover:bg-slate-50"
            data-testid="pf-copy-link"
          >
            <ExternalLink className="w-4 h-4 text-purple-600" />
            <span>Copy Link</span>
          </button>
          <a
            href={`/position-folders?pf=${selectedPf.pf_number}`}
            className="flex flex-col items-center gap-1 p-2 border rounded-lg text-xs hover:bg-slate-50"
            data-testid="pf-open-web"
          >
            <FolderOpen className="w-4 h-4 text-blue-600" />
            <span>Open Web</span>
          </a>
          <button
            onClick={() => fetchPfDetails(selectedPf.id)}
            className="flex flex-col items-center gap-1 p-2 border rounded-lg text-xs hover:bg-slate-50"
            data-testid="pf-refresh"
          >
            <RefreshCw className="w-4 h-4 text-slate-600" />
            <span>Refresh</span>
          </button>
          {selectedPf.customer_email && (
            <button
              onClick={() => setShowPfEmailModal(true)}
              className="flex flex-col items-center gap-1 p-2 border rounded-lg text-xs hover:bg-slate-50 col-span-2"
              data-testid="pf-tracked-email-btn"
            >
              <Mail className="w-4 h-4 text-purple-600" />
              <span>Send tracked email to {selectedPf.customer_name?.split(" ")[0] || "customer"}</span>
            </button>
          )}
        </div>
      </div>

      {/* PF Tracked Email Modal — sends via /position-folders/{id}/send-email
          so the send is audit-logged on the PF (unlike the mailto: button
          above which just opens the device's mail app). */}
      {showPfEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end z-50" data-testid="pf-email-modal">
          <div className="bg-white w-full rounded-t-2xl p-4 pb-8 animate-slide-up max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Mail className="w-4 h-4 text-purple-600" />
                Email · {selectedPf?.pf_number}
              </h3>
              <Button size="sm" variant="ghost" onClick={() => setShowPfEmailModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="mb-3 space-y-2">
              <div>
                <div className="text-xs font-medium text-slate-600 mb-1">To (comma-separated)</div>
                <input
                  type="text"
                  value={pfEmailTo}
                  onChange={(e) => setPfEmailTo(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded border border-slate-200"
                  data-testid="pf-email-to"
                />
              </div>
              <div>
                <div className="text-xs font-medium text-slate-600 mb-1">Cc (comma-separated)</div>
                <input
                  type="text"
                  value={pfEmailCc}
                  onChange={(e) => setPfEmailCc(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded border border-slate-200"
                  data-testid="pf-email-cc"
                />
              </div>
            </div>
            <div className="mb-3">
              <div className="text-xs font-medium text-slate-600 mb-1">Quick template</div>
              <select
                onChange={(e) => { if (e.target.value) applyPfTemplate(e.target.value); }}
                defaultValue=""
                className="w-full px-3 py-2 text-sm rounded border border-slate-200 bg-white"
                data-testid="pf-email-template"
              >
                <option value="">— pick a template —</option>
                {PF_QUICK_TEMPLATES.map(t => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <div className="text-xs font-medium text-slate-600 mb-1">Subject</div>
              <input
                type="text"
                value={pfEmailSubject}
                onChange={(e) => setPfEmailSubject(e.target.value)}
                placeholder={`Re: ${selectedPf?.pf_number || ""}`}
                className="w-full px-3 py-2 text-sm rounded border border-slate-200"
                data-testid="pf-email-subject"
              />
            </div>
            <Textarea
              placeholder="Type your message…"
              value={pfEmailBody}
              onChange={(e) => setPfEmailBody(e.target.value)}
              rows={6}
              className="mb-3 font-mono text-sm"
              data-testid="pf-email-body"
            />
            <Button
              className="w-full bg-purple-600 hover:bg-purple-700"
              onClick={sendPfTrackedEmail}
              disabled={pfEmailSending || !pfEmailBody.trim()}
              data-testid="pf-email-send-btn"
            >
              {pfEmailSending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Email
            </Button>
          </div>
        </div>
      )}

      {/* Add Comment Modal */}
      {showCommentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end z-50">
          <div className="bg-white w-full rounded-t-2xl p-4 pb-8 animate-slide-up">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Add Comment</h3>
              <Button size="sm" variant="ghost" onClick={() => setShowCommentModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex gap-2 mb-3 text-xs">
              <button
                onClick={() => setCommentAudience("customer")}
                className={`flex-1 py-2 rounded-lg border ${commentAudience === "customer" ? "bg-blue-50 border-blue-300 text-blue-700" : ""}`}
                data-testid="comment-audience-customer"
              >
                👤 Customer-visible
              </button>
              <button
                onClick={() => setCommentAudience("master")}
                className={`flex-1 py-2 rounded-lg border ${commentAudience === "master" ? "bg-amber-50 border-amber-300 text-amber-700" : ""}`}
                data-testid="comment-audience-master"
              >
                🔒 Internal only
              </button>
            </div>
            <Textarea
              placeholder="Add a comment (max 2000 chars)…"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              rows={3}
              className="mb-3"
              data-testid="comment-textarea"
            />
            <Button
              className="w-full"
              onClick={submitComment}
              disabled={submitting || !commentText.trim()}
              data-testid="comment-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Add Comment
            </Button>
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      {showAddTaskModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end z-50">
          <div className="bg-white w-full rounded-t-2xl p-4 pb-8 animate-slide-up">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Add Task</h3>
              <Button size="sm" variant="ghost" onClick={() => setShowAddTaskModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <Input
              placeholder="Task description"
              value={newTaskLabel}
              onChange={(e) => setNewTaskLabel(e.target.value)}
              className="mb-3"
              data-testid="task-label-input"
            />
            <Button
              className="w-full"
              onClick={submitAddTask}
              disabled={submitting || !newTaskLabel.trim()}
              data-testid="task-add-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Task
            </Button>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end z-50">
          <div className="bg-white w-full rounded-t-2xl p-4 pb-8 animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Add Status Update</h3>
              <Button size="sm" variant="ghost" onClick={() => setShowStatusModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <Textarea
              placeholder="Enter status update (e.g., 'Vessel delayed - new ETA Jan 15')"
              value={statusUpdate}
              onChange={(e) => setStatusUpdate(e.target.value)}
              rows={3}
              className="mb-3"
            />
            <Button 
              className="w-full" 
              onClick={submitStatusUpdate}
              disabled={submitting || !statusUpdate.trim()}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit Update
            </Button>
          </div>
        </div>
      )}

      {/* Transport Message Modal — moved to Transport Orders tab on 2026-05-17 */}
    </div>
  );
}

// Leave Calendar Tab for Mobile
function LeaveTab({ token }) {
  const [loading, setLoading] = useState(true);
  const [staffOnLeave, setStaffOnLeave] = useState([]);
  const [myLeaves, setMyLeaves] = useState([]);
  const [unhandledTasks, setUnhandledTasks] = useState([]);

  useEffect(() => {
    fetchLeaveData();
  }, []);

  const fetchLeaveData = async () => {
    setLoading(true);
    try {
      const [onLeaveRes, entriesRes, tasksRes] = await Promise.all([
        axios.get(`${API_URL}/leave/staff-on-leave`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/leave/entries`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/leave/unhandled-tasks`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStaffOnLeave(onLeaveRes.data.staff_on_leave || []);
      setMyLeaves(entriesRes.data.entries || []);
      setUnhandledTasks(tasksRes.data.unhandled_tasks || []);
    } catch (error) {
      console.error("Error loading leave data:", error);
      toast.error("Failed to load leave data");
    } finally {
      setLoading(false);
    }
  };

  const LEAVE_COLORS = {
    holiday_leave: "bg-green-100 text-green-800 border-green-200",
    sick_leave: "bg-red-100 text-red-800 border-red-200",
    non_paid_leave: "bg-amber-100 text-amber-800 border-amber-200",
    late_arrival: "bg-blue-100 text-blue-800 border-blue-200",
    early_departure: "bg-purple-100 text-purple-800 border-purple-200",
    work_from_home: "bg-cyan-100 text-cyan-800 border-cyan-200",
    business_trip: "bg-indigo-100 text-indigo-800 border-indigo-200",
    other_absence: "bg-slate-100 text-slate-800 border-slate-200",
  };

  const LEAVE_LABELS = {
    holiday_leave: "Holiday",
    sick_leave: "Sick",
    non_paid_leave: "Non-Paid",
    late_arrival: "Late",
    early_departure: "Early Out",
    work_from_home: "WFH",
    business_trip: "Trip",
    other_absence: "Other",
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-5 text-white">
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Calendar className="w-6 h-6" />
          Leave Calendar
        </h1>
        <p className="text-indigo-100 text-sm mt-1">Staff absences and coverage</p>
      </div>

      {/* Unhandled Tasks Alert */}
      {unhandledTasks.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-amber-800 font-semibold mb-2">
            <AlertCircle className="w-5 h-5" />
            Tasks Need Attention
          </div>
          {unhandledTasks.map((task, idx) => (
            <div key={idx} className="bg-white rounded-lg p-3 mb-2 border border-amber-100">
              <div className="font-medium">{task.staff_name}</div>
              <div className="text-sm text-amber-700">
                {task.pf_count} PF(s) • {task.leave_type_label}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Staff Out Today */}
      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Users className="w-5 h-5 text-slate-600" />
          Out Today ({staffOnLeave.length})
        </h2>
        {staffOnLeave.length > 0 ? (
          <div className="space-y-2">
            {staffOnLeave.map((item, idx) => (
              <div 
                key={idx} 
                className={`rounded-xl p-4 border ${LEAVE_COLORS[item.leave_type] || LEAVE_COLORS.other_absence}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{item.staff_name}</p>
                    <p className="text-sm opacity-80">{item.department}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${LEAVE_COLORS[item.leave_type] || LEAVE_COLORS.other_absence}`}>
                    {LEAVE_LABELS[item.leave_type] || item.leave_type}
                  </span>
                </div>
                <div className="text-xs mt-2 opacity-70">
                  Until {new Date(item.end_date).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center text-green-700">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
            All staff present today
          </div>
        )}
      </div>

      {/* Upcoming Leaves */}
      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-slate-600" />
          Upcoming Leaves
        </h2>
        {myLeaves.filter(l => l.start_date > new Date().toISOString().split("T")[0]).slice(0, 5).length > 0 ? (
          <div className="space-y-2">
            {myLeaves.filter(l => l.start_date > new Date().toISOString().split("T")[0]).slice(0, 5).map((leave, idx) => (
              <div key={idx} className="bg-white rounded-xl p-4 border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{leave.staff_name}</p>
                    <p className="text-sm text-slate-500">
                      {new Date(leave.start_date).toLocaleDateString()} - {new Date(leave.end_date).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${LEAVE_COLORS[leave.leave_type] || LEAVE_COLORS.other_absence}`}>
                    {LEAVE_LABELS[leave.leave_type] || leave.leave_type}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-slate-50 rounded-xl p-4 text-center text-slate-500">
            No upcoming leaves scheduled
          </div>
        )}
      </div>
    </div>
  );
}

// POD Upload Tab
function PodUploadTab({ token }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    fetchOrdersNeedingPod();
  }, []);

  const fetchOrdersNeedingPod = async () => {
    try {
      const response = await axios.get(`${API_URL}/staff/mobile/orders-needing-pod`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error("Failed to fetch orders", error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Preview the image
      const reader = new FileReader();
      reader.onload = () => {
        setCapturedImage({
          preview: reader.result,
          file: file
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPod = async () => {
    if (!selectedOrder || !capturedImage) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', capturedImage.file);

      await axios.post(
        `${API_URL}/staff/mobile/upload-pod/${selectedOrder.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      toast.success(`POD uploaded for ${selectedOrder.order_number}`);
      setCapturedImage(null);
      setSelectedOrder(null);
      fetchOrdersNeedingPod();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Image capture/preview mode
  if (selectedOrder && capturedImage) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex flex-col">
        <div className="flex items-center justify-between p-4 text-white">
          <button onClick={() => setCapturedImage(null)} className="p-2">
            <X className="w-6 h-6" />
          </button>
          <span className="font-medium">Preview POD</span>
          <div className="w-10" />
        </div>
        
        <div className="flex-1 flex items-center justify-center p-4">
          <img 
            src={capturedImage.preview} 
            alt="POD Preview" 
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>

        <div className="p-4 pb-safe bg-black/50">
          <div className="text-white text-center mb-4">
            <p className="font-medium">{selectedOrder.order_number}</p>
            <p className="text-sm text-slate-300">{selectedOrder.destination}</p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1 bg-transparent text-white border-white"
              onClick={() => setCapturedImage(null)}
            >
              Retake
            </Button>
            <Button 
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={uploadPod}
              disabled={uploading}
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Order selection for POD upload
  if (selectedOrder) {
    return (
      <div className="space-y-4 pb-24">
        <div className="flex items-center gap-3">
          <button onClick={() => setSelectedOrder(null)} className="p-2 -ml-2">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div>
            <h2 className="text-lg font-bold">Upload POD</h2>
            <p className="text-sm text-slate-500">{selectedOrder.order_number}</p>
          </div>
        </div>

        {/* Order Info */}
        <div className="bg-white rounded-xl border p-4">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-500">PF Number</span>
              <span className="font-medium">{selectedOrder.pf_number || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Destination</span>
              <span className="font-medium">{selectedOrder.destination || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Trucking Co.</span>
              <span className="font-medium">{selectedOrder.trucking_company?.name || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Delivery Date</span>
              <span className="font-medium">
                {selectedOrder.actual_delivery_date?.split('T')[0] || selectedOrder.date_delivery?.split('T')[0] || "-"}
              </span>
            </div>
          </div>
        </div>

        {/* Upload Options */}
        <div className="space-y-3">
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleFileSelect}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={handleFileSelect}
          />

          <Button 
            className="w-full h-auto py-6 flex flex-col items-center gap-2 bg-blue-600 hover:bg-blue-700"
            onClick={() => cameraInputRef.current?.click()}
            data-testid="take-photo-btn"
          >
            <Camera className="w-10 h-10" />
            <span className="text-lg font-medium">Take Photo</span>
            <span className="text-sm text-blue-200">Use camera to capture POD</span>
          </Button>

          <Button 
            variant="outline"
            className="w-full h-auto py-4 flex items-center justify-center gap-2"
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-file-btn"
          >
            <FileImage className="w-5 h-5" />
            <span>Choose from Gallery</span>
          </Button>
        </div>
      </div>
    );
  }

  // Orders list
  return (
    <div className="space-y-4 pb-24">
      <h2 className="text-lg font-semibold">Orders Needing POD</h2>
      
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>All PODs are uploaded!</p>
          <p className="text-sm mt-1">No orders pending POD</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <button
              key={order.id}
              onClick={() => setSelectedOrder(order)}
              className="w-full bg-white rounded-xl border p-4 text-left active:scale-98 transition-transform"
              data-testid={`pod-order-${order.order_number}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-slate-800">{order.order_number}</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700">
                      POD Needed
                    </span>
                  </div>
                  {order.pf_number && (
                    <p className="text-sm text-blue-600 mt-1">{order.pf_number}</p>
                  )}
                  <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                    <MapPin className="w-3 h-3" />
                    <span>{order.destination || "Destination pending"}</span>
                  </div>
                </div>
                <Camera className="w-5 h-5 text-orange-500" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// CRM Note Tab
function CrmNoteTab({ token }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState({ companies: [], contacts: [] });
  const [searching, setSearching] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [noteType, setNoteType] = useState("call");
  const [followUpDate, setFollowUpDate] = useState("");
  const [saving, setSaving] = useState(false);

  const searchCrm = async (query) => {
    if (query.length < 2) {
      setSearchResults({ companies: [], contacts: [] });
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(`${API_URL}/staff/mobile/crm-search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error("Search failed", error);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (searchQuery) searchCrm(searchQuery);
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const saveNote = async () => {
    if (!noteText.trim()) {
      toast.error("Please enter a note");
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/staff/mobile/add-crm-note`,
        {
          contact_id: selectedEntity?.type === "contact" ? selectedEntity.id : null,
          company_id: selectedEntity?.type === "company" ? selectedEntity.id : null,
          company_name: selectedEntity?.name || selectedEntity?.company_name,
          note_text: noteText.trim(),
          note_type: noteType,
          follow_up_date: followUpDate || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Note saved!");
      setNoteText("");
      setSelectedEntity(null);
      setFollowUpDate("");
    } catch (error) {
      toast.error("Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 pb-24">
      <h2 className="text-lg font-semibold">Add CRM Note</h2>

      {/* Search CRM */}
      <div className="bg-white rounded-xl border p-4">
        <label className="text-sm text-slate-600 mb-2 block">Company or Contact</label>
        <div className="relative">
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search company or contact..."
            className="pr-10"
            data-testid="crm-search-input"
          />
          {searching && (
            <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
          )}
        </div>

        {/* Search Results */}
        {(searchResults.companies.length > 0 || searchResults.contacts.length > 0) && !selectedEntity && (
          <div className="mt-3 border-t pt-3 space-y-2 max-h-48 overflow-y-auto">
            {searchResults.companies.map((company) => (
              <button
                key={company.id}
                onClick={() => {
                  setSelectedEntity({ ...company, type: "company" });
                  setSearchQuery("");
                  setSearchResults({ companies: [], contacts: [] });
                }}
                className="w-full p-2 text-left rounded-lg hover:bg-slate-50 flex items-center gap-2"
              >
                <Building2 className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="font-medium text-sm">{company.name}</p>
                  <p className="text-xs text-slate-500">{company.city}, {company.country}</p>
                </div>
              </button>
            ))}
            {searchResults.contacts.map((contact) => (
              <button
                key={contact.id}
                onClick={() => {
                  setSelectedEntity({ ...contact, type: "contact" });
                  setSearchQuery("");
                  setSearchResults({ companies: [], contacts: [] });
                }}
                className="w-full p-2 text-left rounded-lg hover:bg-slate-50 flex items-center gap-2"
              >
                <User className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="font-medium text-sm">{contact.name}</p>
                  <p className="text-xs text-slate-500">{contact.company_name}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Selected Entity */}
        {selectedEntity && (
          <div className="mt-3 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              {selectedEntity.type === "company" ? (
                <Building2 className="w-4 h-4 text-blue-600" />
              ) : (
                <User className="w-4 h-4 text-blue-600" />
              )}
              <span className="font-medium text-blue-900">{selectedEntity.name}</span>
            </div>
            <button onClick={() => setSelectedEntity(null)} className="text-blue-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Note Type */}
      <div className="bg-white rounded-xl border p-4">
        <label className="text-sm text-slate-600 mb-2 block">Note Type</label>
        <div className="flex gap-2">
          {[
            { id: "call", label: "Call", icon: Phone },
            { id: "meeting", label: "Meeting", icon: Calendar },
            { id: "email", label: "Email", icon: Mail },
            { id: "general", label: "Other", icon: FileText },
          ].map((type) => (
            <button
              key={type.id}
              onClick={() => setNoteType(type.id)}
              className={`flex-1 flex flex-col items-center py-3 rounded-lg transition-colors ${
                noteType === type.id
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 text-slate-600"
              }`}
              data-testid={`note-type-${type.id}`}
            >
              <type.icon className="w-5 h-5" />
              <span className="text-xs mt-1">{type.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Note Content */}
      <div className="bg-white rounded-xl border p-4">
        <label className="text-sm text-slate-600 mb-2 block">Note</label>
        <Textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Enter your note here..."
          rows={4}
          data-testid="note-text-input"
        />
      </div>

      {/* Follow-up */}
      <div className="bg-white rounded-xl border p-4">
        <label className="text-sm text-slate-600 mb-2 block">Follow-up Reminder (optional)</label>
        <Input
          type="datetime-local"
          value={followUpDate}
          onChange={(e) => setFollowUpDate(e.target.value)}
          data-testid="follow-up-date"
        />
      </div>

      {/* Save Button */}
      <Button 
        className="w-full" 
        onClick={saveNote}
        disabled={saving || !noteText.trim()}
        data-testid="save-note-btn"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
        Save Note
      </Button>
    </div>
  );
}

// Voice Notes Tab - Audio recording and transcription
function VoiceNotesTab({ token }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [savedNotes, setSavedNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteTitle, setNoteTitle] = useState("");
  const [linkedPF, setLinkedPF] = useState("");
  const [linkedCompany, setLinkedCompany] = useState("");
  const [saving, setSaving] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    fetchSavedNotes();
    return () => {
      // Cleanup
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const fetchSavedNotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/voice-notes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSavedNotes(response.data.notes || []);
    } catch (error) {
      console.error("Failed to fetch voice notes", error);
    } finally {
      setLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const url = URL.createObjectURL(audioBlob);
        setAudioURL(url);
      };

      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.success("Recording started");
    } catch (error) {
      toast.error("Could not access microphone");
      console.error(error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      setIsRecording(false);
      setIsPaused(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      toast.success("Recording stopped");
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
        setIsPaused(false);
      } else {
        mediaRecorderRef.current.pause();
        if (timerRef.current) clearInterval(timerRef.current);
        setIsPaused(true);
      }
    }
  };

  const transcribeAudio = async () => {
    if (!audioURL) return;
    
    setIsTranscribing(true);
    try {
      // Get the blob from URL
      const response = await fetch(audioURL);
      const blob = await response.blob();
      
      const formData = new FormData();
      formData.append("audio", blob, "recording.webm");
      
      const result = await axios.post(`${API_URL}/voice-notes/transcribe`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        },
      });
      
      setTranscript(result.data.transcript || "No transcript available");
      toast.success("Transcription complete!");
    } catch (error) {
      toast.error("Transcription failed - AI service unavailable");
      // Fallback: manual transcript entry
      setTranscript("[Transcription service unavailable - please enter notes manually]");
    } finally {
      setIsTranscribing(false);
    }
  };

  const saveVoiceNote = async () => {
    if (!audioURL || !noteTitle) {
      toast.error("Please record audio and add a title");
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(audioURL);
      const blob = await response.blob();
      
      const formData = new FormData();
      formData.append("audio", blob, "recording.webm");
      formData.append("title", noteTitle);
      formData.append("transcript", transcript);
      if (linkedPF) formData.append("linked_pf", linkedPF);
      if (linkedCompany) formData.append("linked_company", linkedCompany);
      formData.append("duration", recordingTime.toString());
      
      await axios.post(`${API_URL}/voice-notes`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        },
      });
      
      toast.success("Voice note saved!");
      
      // Reset form
      setAudioURL(null);
      setTranscript("");
      setNoteTitle("");
      setLinkedPF("");
      setLinkedCompany("");
      setRecordingTime(0);
      fetchSavedNotes();
    } catch (error) {
      toast.error("Failed to save voice note");
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this voice note?")) return;
    try {
      await axios.delete(`${API_URL}/voice-notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Note deleted");
      fetchSavedNotes();
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="p-4 pb-24 space-y-4">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <Mic className="w-5 h-5" />
        Voice Notes
      </h2>
      
      {/* Recording Section */}
      <div className="bg-white rounded-xl p-4 shadow-sm border">
        <h3 className="font-medium mb-4">New Recording</h3>
        
        {/* Recording Controls */}
        <div className="flex items-center justify-center gap-4 py-4">
          {!isRecording && !audioURL && (
            <button
              onClick={startRecording}
              className="w-20 h-20 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors"
            >
              <Mic className="w-8 h-8" />
            </button>
          )}
          
          {isRecording && (
            <>
              <button
                onClick={pauseRecording}
                className="w-14 h-14 rounded-full bg-yellow-500 text-white flex items-center justify-center shadow-lg"
              >
                {isPaused ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
              </button>
              <button
                onClick={stopRecording}
                className="w-20 h-20 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg animate-pulse"
              >
                <Square className="w-8 h-8" />
              </button>
            </>
          )}
        </div>
        
        {/* Recording Timer */}
        {(isRecording || audioURL) && (
          <div className="text-center text-2xl font-mono text-slate-600">
            {formatTime(recordingTime)}
            {isPaused && <span className="text-yellow-500 ml-2">(Paused)</span>}
          </div>
        )}
        
        {/* Audio Playback */}
        {audioURL && !isRecording && (
          <div className="mt-4 space-y-3">
            <audio src={audioURL} controls className="w-full" />
            
            <Button
              onClick={transcribeAudio}
              disabled={isTranscribing}
              variant="outline"
              className="w-full"
            >
              {isTranscribing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Transcribing...</>
              ) : (
                <><FileText className="w-4 h-4 mr-2" /> Get AI Transcript</>
              )}
            </Button>
            
            {/* Transcript */}
            {transcript && (
              <div className="bg-slate-50 rounded-lg p-3">
                <label className="text-xs text-slate-500 block mb-1">Transcript:</label>
                <Textarea
                  value={transcript}
                  onChange={(e) => setTranscript(e.target.value)}
                  rows={4}
                  className="text-sm"
                />
              </div>
            )}
            
            {/* Note Details */}
            <Input
              placeholder="Note title (e.g., Sales call with ABC Ltd)"
              value={noteTitle}
              onChange={(e) => setNoteTitle(e.target.value)}
            />
            
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Link to PF# (optional)"
                value={linkedPF}
                onChange={(e) => setLinkedPF(e.target.value)}
              />
              <Input
                placeholder="Company name (optional)"
                value={linkedCompany}
                onChange={(e) => setLinkedCompany(e.target.value)}
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setAudioURL(null);
                  setTranscript("");
                  setRecordingTime(0);
                }}
                variant="outline"
                className="flex-1"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Discard
              </Button>
              <Button
                onClick={saveVoiceNote}
                disabled={saving || !noteTitle}
                className="flex-1"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Save Note
              </Button>
            </div>
          </div>
        )}
      </div>
      
      {/* Saved Notes */}
      <div className="bg-white rounded-xl p-4 shadow-sm border">
        <h3 className="font-medium mb-3">Saved Voice Notes</h3>
        
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
          </div>
        ) : savedNotes.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Mic className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No voice notes yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {savedNotes.map((note) => (
              <div key={note.id} className="border rounded-lg p-3">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium text-sm">{note.title}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(note.created_at).toLocaleString()} • {formatTime(note.duration || 0)}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteNote(note.id)}
                    className="text-red-500 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {note.linked_pf && (
                  <p className="text-xs text-blue-600 mb-1">📁 PF: {note.linked_pf}</p>
                )}
                {note.linked_company && (
                  <p className="text-xs text-green-600 mb-1">🏢 {note.linked_company}</p>
                )}
                {note.transcript && (
                  <p className="text-xs text-slate-600 bg-slate-50 p-2 rounded mt-2 line-clamp-3">
                    {note.transcript}
                  </p>
                )}
                {note.audio_url && (
                  <audio src={note.audio_url} controls className="w-full mt-2 h-8" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// 2026-05-16 — Staff Sticky Notes (personal / team / per-PF scratchpad)
function StickyNotesTab({ token, user }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("all"); // all | personal | team
  const [showCompose, setShowCompose] = useState(false);
  const [draft, setDraft] = useState({ content: "", scope: "personal", color: "yellow", pinned: false, pf_id: "", pf_number: "" });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const COLORS = {
    yellow: { bg: "bg-yellow-100", border: "border-yellow-300", text: "text-yellow-900" },
    blue:   { bg: "bg-blue-100",   border: "border-blue-300",   text: "text-blue-900" },
    green:  { bg: "bg-green-100",  border: "border-green-300",  text: "text-green-900" },
    pink:   { bg: "bg-pink-100",   border: "border-pink-300",   text: "text-pink-900" },
    orange: { bg: "bg-orange-100", border: "border-orange-300", text: "text-orange-900" },
    purple: { bg: "bg-purple-100", border: "border-purple-300", text: "text-purple-900" },
  };

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const params = scope === "all" ? "" : `?scope=${scope}`;
      const r = await axios.get(`${API_URL}/staff/sticky-notes${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotes(r.data.notes || []);
    } catch (e) {
      toast.error("Could not load sticky notes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotes(); /* eslint-disable-next-line */ }, [scope]);

  const submit = async () => {
    if (!draft.content.trim()) {
      toast.error("Note content cannot be empty");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        content: draft.content.trim(),
        scope: draft.scope,
        color: draft.color,
        pinned: !!draft.pinned,
        pf_id: draft.scope === "pf" ? (draft.pf_id || null) : null,
        pf_number: draft.scope === "pf" ? (draft.pf_number || null) : null,
      };
      if (editingId) {
        await axios.patch(`${API_URL}/staff/sticky-notes/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Note updated");
      } else {
        await axios.post(`${API_URL}/staff/sticky-notes`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Note saved");
      }
      setDraft({ content: "", scope: "personal", color: "yellow", pinned: false, pf_id: "", pf_number: "" });
      setShowCompose(false);
      setEditingId(null);
      fetchNotes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const togglePin = async (note) => {
    try {
      await axios.patch(`${API_URL}/staff/sticky-notes/${note.id}`, { pinned: !note.pinned }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchNotes();
    } catch {
      toast.error("Could not toggle pin");
    }
  };

  const remove = async (note) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await axios.delete(`${API_URL}/staff/sticky-notes/${note.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Deleted");
      fetchNotes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setDraft({
      content: note.content || "",
      scope: note.scope || "personal",
      color: note.color || "yellow",
      pinned: !!note.pinned,
      pf_id: note.pf_id || "",
      pf_number: note.pf_number || "",
    });
    setShowCompose(true);
  };

  return (
    <div className="space-y-4 pb-20" data-testid="sticky-notes-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <StickyNote className="w-5 h-5 text-amber-500" />
          Sticky Notes
        </h2>
        <Button
          size="sm"
          onClick={() => { setEditingId(null); setDraft({ content: "", scope: "personal", color: "yellow", pinned: false, pf_id: "", pf_number: "" }); setShowCompose(true); }}
          className="bg-amber-500 hover:bg-amber-600 text-white"
          data-testid="sticky-add-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New
        </Button>
      </div>

      {/* Scope filter */}
      <div className="flex gap-2 bg-white rounded-xl p-1 border" data-testid="sticky-scope-filter">
        {[
          { id: "all", label: "All" },
          { id: "personal", label: "Mine" },
          { id: "team", label: "Team" },
        ].map(s => (
          <button
            key={s.id}
            onClick={() => setScope(s.id)}
            className={`flex-1 py-2 text-xs font-medium rounded-lg transition-colors ${
              scope === s.id ? "bg-amber-500 text-white" : "text-slate-600"
            }`}
            data-testid={`sticky-scope-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Compose sheet */}
      {showCompose && (
        <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="sticky-compose">
          <textarea
            className="w-full border rounded-lg p-3 text-sm min-h-[100px]"
            placeholder="Write your note…"
            value={draft.content}
            onChange={(e) => setDraft({ ...draft, content: e.target.value })}
            maxLength={2000}
            autoFocus
            data-testid="sticky-content"
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500 mr-1">Scope:</span>
            {["personal", "team", "pf"].map(s => (
              <button
                key={s}
                onClick={() => setDraft({ ...draft, scope: s })}
                className={`px-3 py-1 rounded-full text-xs border ${
                  draft.scope === s ? "bg-blue-500 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300"
                }`}
                data-testid={`sticky-draft-scope-${s}`}
              >
                {s === "personal" ? "Mine" : s === "team" ? "Team" : "PF-pin"}
              </button>
            ))}
          </div>
          {draft.scope === "pf" && (
            <Input
              placeholder="PF number (e.g. PF-000543-26)"
              value={draft.pf_number}
              onChange={(e) => setDraft({ ...draft, pf_number: e.target.value, pf_id: draft.pf_id || e.target.value })}
              data-testid="sticky-pf-number"
            />
          )}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500 mr-1">Color:</span>
            {Object.keys(COLORS).map(c => (
              <button
                key={c}
                onClick={() => setDraft({ ...draft, color: c })}
                className={`w-7 h-7 rounded-full border-2 ${COLORS[c].bg} ${
                  draft.color === c ? "border-slate-700 scale-110" : "border-transparent"
                } transition-transform`}
                title={c}
                aria-label={`color-${c}`}
                data-testid={`sticky-color-${c}`}
              />
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.pinned}
              onChange={(e) => setDraft({ ...draft, pinned: e.target.checked })}
              data-testid="sticky-pinned"
            />
            Pin to top
          </label>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowCompose(false); setEditingId(null); }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={submit}
              disabled={saving}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="sticky-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? "Update" : "Save")}
            </Button>
          </div>
        </div>
      )}

      {/* Notes list */}
      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : notes.length === 0 ? (
        <div className="bg-white rounded-xl border p-8 text-center text-slate-400">
          <StickyNote className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p className="text-sm">No notes yet — tap “New” to add one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {notes.map(n => {
            const c = COLORS[n.color] || COLORS.yellow;
            const isMine = n.created_by === user?.id;
            return (
              <div
                key={n.id}
                className={`${c.bg} ${c.border} ${c.text} border rounded-xl p-4 shadow-sm`}
                data-testid={`sticky-note-${n.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-70">
                    <span>{n.scope === "personal" ? "MINE" : n.scope === "team" ? "TEAM" : "PF"}</span>
                    {n.pf_number && <span>· {n.pf_number}</span>}
                    {n.pinned && <span>📌</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => togglePin(n)} className="p-1 hover:bg-black/5 rounded" title="Pin/unpin" data-testid={`sticky-pin-${n.id}`}>
                      <Star className={`w-4 h-4 ${n.pinned ? "fill-current" : ""}`} />
                    </button>
                    {(isMine || n.scope !== "personal") && (
                      <>
                        <button onClick={() => startEdit(n)} className="p-1 hover:bg-black/5 rounded" title="Edit" data-testid={`sticky-edit-${n.id}`}>
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button onClick={() => remove(n)} className="p-1 hover:bg-black/5 rounded" title="Delete" data-testid={`sticky-delete-${n.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <p className="text-sm whitespace-pre-wrap break-words">{n.content}</p>
                <div className="mt-2 text-[10px] opacity-60">
                  {n.created_by_name} · {new Date(n.updated_at).toLocaleString()}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// Staff AI Chat Panel — in-app Roket assistant for ops questions
function StaffAIChatPanel({ token }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() =>
    localStorage.getItem("staff_ai_session_id") || ""
  );
  const scrollerRef = useRef(null);

  useEffect(() => {
    (async () => {
      if (sessionId) {
        try {
          const r = await axios.get(
            `${API_URL}/staff/ai-chat/${sessionId}/history`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          const turns = (r.data?.messages || []).map((m, i) => ({
            id: `${sessionId}-${i}`,
            role: m.role, text: m.text, at: m.at,
          }));
          setMessages(turns.length ? turns : seedGreeting());
        } catch {
          setMessages(seedGreeting());
        }
      } else {
        setMessages(seedGreeting());
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seedGreeting = () => [{
    id: "welcome",
    role: "assistant",
    text: "Hey 👋 I'm Roket. Ask me about today's open PFs, pending tickets, customers' CO₂ footprint, or anything else operational. I see live company data.",
    at: new Date().toISOString(),
  }];

  useEffect(() => {
    scrollerRef.current?.scrollTo(0, scrollerRef.current.scrollHeight);
  }, [messages]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const userText = input.trim();
    setInput("");
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      role: "user", text: userText, at: new Date().toISOString(),
    }]);
    setSending(true);
    try {
      const r = await axios.post(`${API_URL}/staff/ai-chat`,
        { message: userText, session_id: sessionId || undefined },
        { headers: { Authorization: `Bearer ${token}` } });
      if (r.data?.session_id && r.data.session_id !== sessionId) {
        setSessionId(r.data.session_id);
        localStorage.setItem("staff_ai_session_id", r.data.session_id);
      }
      setMessages(prev => [...prev, {
        id: Date.now().toString() + "-r",
        role: "assistant",
        text: r.data?.reply || "(no reply)",
        at: new Date().toISOString(),
      }]);
    } catch (e) {
      setMessages(prev => [...prev, {
        id: Date.now().toString() + "-e",
        role: "assistant",
        text: "Sorry — request failed. Try again in a moment.",
        at: new Date().toISOString(),
      }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col" style={{ minHeight: "60vh" }} data-testid="staff-ai-chat">
      <div ref={scrollerRef} className="flex-1 space-y-2 overflow-y-auto px-1 py-2">
        {messages.map(m => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
              m.role === "user"
                ? "bg-purple-600 text-white"
                : "bg-white border text-slate-800"
            }`}>
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white border text-slate-400 px-3 py-2 rounded-2xl text-sm flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Roket is typing…
            </div>
          </div>
        )}
      </div>
      <div className="flex gap-2 pt-2 border-t bg-white sticky bottom-0">
        <Input
          placeholder="Ask Roket anything…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
          data-testid="staff-ai-input"
        />
        <Button
          onClick={send}
          disabled={!input.trim() || sending}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="staff-ai-send"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}

// Staff Messages Tab
function StaffMessagesTab({ token, onUnreadChange }) {
  const [activeSection, setActiveSection] = useState("whatsapp"); // whatsapp | portal | ai
  // WhatsApp tickets
  const [tickets, setTickets] = useState([]);
  const [ticketStats, setTicketStats] = useState({ pending: 0, resolved: 0, total: 0 });
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [ticketSearch, setTicketSearch] = useState("");
  const [ticketFilter, setTicketFilter] = useState("pending");
  const [selectedTicket, setSelectedTicket] = useState(null);
  // Portal messages (existing)
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchPendingMessages();
    fetchTickets();
    // Poll every 30s for new tickets / messages
    const id = setInterval(() => {
      fetchTickets({ silent: true });
      fetchPendingMessages({ silent: true });
    }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketFilter]);

  const fetchTickets = async ({ silent = false } = {}) => {
    if (!silent) setLoadingTickets(true);
    try {
      const params = new URLSearchParams({ limit: "30" });
      if (ticketFilter !== "all") params.append("status", ticketFilter);
      if (ticketSearch.trim()) params.append("search", ticketSearch.trim());
      const r = await axios.get(`${API_URL}/whatsapp/tickets?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTickets(r.data?.tickets || []);
      setTicketStats(r.data?.stats || { pending: 0, resolved: 0, total: 0 });
      if (onUnreadChange) onUnreadChange(r.data?.stats?.pending || 0);
    } catch (e) {
      if (!silent) console.error("Failed to fetch tickets", e);
    } finally {
      if (!silent) setLoadingTickets(false);
    }
  };

  const fetchPendingMessages = async ({ silent = false } = {}) => {
    try {
      const response = await axios.get(`${API_URL}/staff/mobile/pending-messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      if (!silent) console.error("Failed to fetch messages", error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const sendReply = async () => {
    if (!selectedMessage || !replyText.trim()) return;
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/staff/respond-message/${selectedMessage.id}`,
        { message: replyText.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Reply sent!");
      setReplyText("");
      setSelectedMessage(null);
      fetchPendingMessages();
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  // ── WhatsApp ticket detail view ──────────────────────────────────────
  if (selectedTicket) {
    return (
      <WhatsAppTicketDetail
        ticket={selectedTicket}
        token={token}
        onBack={() => { setSelectedTicket(null); fetchTickets(); }}
      />
    );
  }

  // ── Portal message detail (existing) ─────────────────────────────────
  if (selectedMessage) {
    return (
      <div className="space-y-4 pb-24">
        <div className="flex items-center gap-3">
          <button onClick={() => setSelectedMessage(null)} className="p-2 -ml-2">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div>
            <h2 className="text-lg font-bold">{selectedMessage.pf_number}</h2>
            <p className="text-sm text-slate-500">{selectedMessage.company_name}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-4 h-4 text-slate-400" />
            <span className="font-medium text-sm">{selectedMessage.sender_name}</span>
            <span className="text-xs text-slate-400">{selectedMessage.sender_email}</span>
          </div>
          <p className="text-slate-800">{selectedMessage.message}</p>
          <p className="text-xs text-slate-400 mt-2">
            {new Date(selectedMessage.created_at).toLocaleString()}
          </p>
        </div>

        <div className="bg-white rounded-xl border p-4">
          <label className="text-sm text-slate-600 mb-2 block">Your Reply</label>
          <Textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your reply..."
            rows={4}
            data-testid="reply-text"
          />
        </div>

        <Button
          className="w-full"
          onClick={sendReply}
          disabled={sending || !replyText.trim()}
          data-testid="send-reply-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
          Send Reply
        </Button>
      </div>
    );
  }

  // ── Inbox list view ──────────────────────────────────────────────────
  return (
    <div className="space-y-4 pb-24">
      <h2 className="text-lg font-semibold">Inbox</h2>

      {/* Section tabs */}
      <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded-lg p-1">
        <button
          onClick={() => setActiveSection("whatsapp")}
          className={`relative py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            activeSection === "whatsapp" ? "bg-white text-blue-600 shadow-sm" : "text-slate-600"
          }`}
          data-testid="section-whatsapp"
        >
          WhatsApp
          {ticketStats.pending > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {ticketStats.pending}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveSection("portal")}
          className={`relative py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            activeSection === "portal" ? "bg-white text-blue-600 shadow-sm" : "text-slate-600"
          }`}
          data-testid="section-portal"
        >
          Portal
          {messages.length > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {messages.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveSection("ai")}
          className={`relative py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            activeSection === "ai" ? "bg-white text-purple-600 shadow-sm" : "text-slate-600"
          }`}
          data-testid="section-ai"
        >
          <span className="inline-flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5" /> Ask Roket
          </span>
        </button>
      </div>

      {/* Ask Roket — staff AI assistant */}
      {activeSection === "ai" && (
        <StaffAIChatPanel token={token} />
      )}

      {/* WhatsApp tickets section */}
      {activeSection === "whatsapp" && (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search ticket / phone / PF / customer..."
              value={ticketSearch}
              onChange={(e) => setTicketSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") fetchTickets(); }}
              className="pl-9"
              data-testid="ticket-search"
            />
          </div>

          <div className="flex gap-2 overflow-x-auto pb-1">
            {[
              { id: "pending", label: `Pending (${ticketStats.pending})` },
              { id: "in_progress", label: "In progress" },
              { id: "resolved", label: `Resolved (${ticketStats.resolved})` },
              { id: "all", label: `All (${ticketStats.total})` },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setTicketFilter(f.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                  ticketFilter === f.id ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"
                }`}
                data-testid={`ticket-filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {loadingTickets ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No {ticketFilter !== "all" ? ticketFilter.replace("_", " ") : ""} tickets</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tickets.map((t) => {
                const statusColor = {
                  pending: "bg-red-100 text-red-700",
                  in_progress: "bg-blue-100 text-blue-700",
                  resolved: "bg-emerald-100 text-emerald-700",
                  closed: "bg-slate-100 text-slate-600",
                }[t.status] || "bg-slate-100 text-slate-600";
                return (
                  <button
                    key={t.ticket_id}
                    onClick={() => setSelectedTicket(t)}
                    className="w-full bg-white rounded-xl border p-3 text-left active:bg-slate-50"
                    data-testid={`ticket-${t.ticket_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-800 text-sm">{t.customer_name || "(unknown)"}</span>
                          {t.out_of_hours && (
                            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">OOO</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {(t.customer_company || "").slice(0, 30)} {t.customer_company && t.phone_number ? "·" : ""} {t.phone_number?.replace("whatsapp:", "")}
                        </p>
                        {t.related_pf && (
                          <p className="text-xs text-blue-600 mt-0.5 font-medium">→ {t.related_pf}</p>
                        )}
                        <p className="text-[10px] text-slate-400 mt-1 font-mono">{t.ticket_id}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColor}`}>
                          {(t.status || "pending").replace("_", " ")}
                        </span>
                        <span className="text-[10px] text-slate-400">
                          {new Date(t.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Portal messages section (legacy) */}
      {activeSection === "portal" && (
        <>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>All portal messages responded!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((msg) => (
                <button
                  key={msg.id}
                  onClick={() => setSelectedMessage(msg)}
                  className="w-full bg-white rounded-xl border p-4 text-left active:scale-98 transition-transform"
                  data-testid={`message-${msg.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-blue-600">{msg.pf_number}</span>
                        <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
                          Pending
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{msg.company_name}</p>
                      <p className="text-sm text-slate-500 mt-1 line-clamp-2">{msg.message}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(msg.created_at).toLocaleString()}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// WhatsApp Ticket Detail — 2026-05-16
function WhatsAppTicketDetail({ ticket, token, onBack }) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(ticket.status || "pending");

  const sendTicketReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/whatsapp/tickets/${ticket.ticket_id}/reply`,
        { message: reply.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("WhatsApp reply sent to customer");
      setReply("");
      // Auto-mark in_progress on first reply
      if (currentStatus === "pending") {
        await updateStatus("in_progress", true);
      }
    } catch (e) {
      toast.error("Failed to send: " + (e.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (newStatus, silent = false) => {
    if (!silent && !window.confirm(`Mark ticket as "${newStatus.replace("_", " ")}"?`)) return;
    setBusy(true);
    try {
      await axios.patch(
        `${API_URL}/whatsapp/tickets/${ticket.ticket_id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurrentStatus(newStatus);
      if (!silent) toast.success(`Ticket marked ${newStatus.replace("_", " ")}`);
    } catch (e) {
      if (!silent) toast.error("Failed to update status");
    } finally {
      setBusy(false);
    }
  };

  // Parse chat_transcript ("[YYYY-MM-DD HH:MM] Role: text\n\n") into messages
  const messages = (ticket.chat_transcript || "")
    .split(/\n\n+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((line, idx) => {
      const m = line.match(/^\[([^\]]+)\]\s*(Customer|Roket Bot|Operator|Staff)?:?\s*(.*)$/s);
      if (m) {
        return {
          idx,
          time: m[1],
          who: m[2] || "Customer",
          text: m[3] || line,
        };
      }
      return { idx, time: "", who: "Customer", text: line };
    });

  const statusColor = {
    pending: "bg-red-100 text-red-700 border-red-200",
    in_progress: "bg-blue-100 text-blue-700 border-blue-200",
    resolved: "bg-emerald-100 text-emerald-700 border-emerald-200",
    closed: "bg-slate-100 text-slate-600 border-slate-200",
  }[currentStatus] || "bg-slate-100 text-slate-600 border-slate-200";

  return (
    <div className="space-y-4 pb-24" data-testid="ticket-detail-view">
      {/* Header */}
      <div className="flex items-center gap-2">
        <button onClick={onBack} className="p-2 -ml-2" data-testid="ticket-back-btn">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-slate-800 truncate">{ticket.customer_name || "(unknown)"}</h2>
          <p className="text-xs text-slate-500 truncate">{ticket.customer_company}</p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusColor}`}>
          {currentStatus.replace("_", " ")}
        </span>
      </div>

      {/* Contact card */}
      <div className="bg-white rounded-xl border p-3 text-sm space-y-1.5">
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-slate-400" />
          <a href={`tel:${ticket.phone_number?.replace("whatsapp:", "")}`} className="text-blue-600">
            {ticket.phone_number?.replace("whatsapp:", "")}
          </a>
        </div>
        {ticket.customer_email && (
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-slate-400" />
            <a href={`mailto:${ticket.customer_email}`} className="text-blue-600 truncate">{ticket.customer_email}</a>
          </div>
        )}
        {ticket.related_pf && (
          <div className="flex items-center gap-2">
            <FolderOpen className="w-4 h-4 text-slate-400" />
            <span className="text-blue-600 font-medium">{ticket.related_pf}</span>
          </div>
        )}
        {ticket.reference_number && (
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-400" />
            <span className="text-slate-700 font-mono text-xs">{ticket.reference_number}</span>
          </div>
        )}
        <div className="text-[10px] text-slate-400 font-mono pt-1 border-t">
          {ticket.ticket_id} · opened {new Date(ticket.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>

      {/* Chat transcript */}
      <div className="bg-white rounded-xl border p-3 space-y-2 max-h-80 overflow-y-auto" data-testid="ticket-transcript">
        {messages.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">No chat history available</p>
        ) : (
          messages.map((m) => {
            const isCustomer = m.who === "Customer";
            return (
              <div key={m.idx} className={`flex ${isCustomer ? "justify-start" : "justify-end"}`}>
                <div className={`max-w-[85%] rounded-lg p-2.5 ${
                  isCustomer ? "bg-slate-100 text-slate-800" : "bg-emerald-100 text-emerald-900"
                }`}>
                  <p className="text-[10px] font-medium opacity-70 mb-0.5">{m.who} · {m.time}</p>
                  <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Reply box */}
      {currentStatus !== "closed" && currentStatus !== "resolved" && (
        <div className="bg-white rounded-xl border p-3">
          <label className="text-xs font-medium text-slate-600 mb-2 block">Reply via WhatsApp</label>
          <Textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Type your reply — customer will receive it on WhatsApp..."
            rows={3}
            className="text-sm"
            data-testid="ticket-reply-input"
          />
          <Button
            onClick={sendTicketReply}
            disabled={sending || !reply.trim()}
            className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700"
            data-testid="ticket-reply-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Send WhatsApp reply
          </Button>
        </div>
      )}

      {/* Status actions */}
      <div className="grid grid-cols-3 gap-2">
        {currentStatus !== "in_progress" && currentStatus !== "resolved" && currentStatus !== "closed" && (
          <Button size="sm" variant="outline" onClick={() => updateStatus("in_progress")} disabled={busy} data-testid="mark-in-progress-btn">
            <Clock className="w-3 h-3 mr-1" /> In progress
          </Button>
        )}
        {currentStatus !== "resolved" && currentStatus !== "closed" && (
          <Button size="sm" variant="outline" className="border-emerald-200 text-emerald-700" onClick={() => updateStatus("resolved")} disabled={busy} data-testid="mark-resolved-btn">
            <CheckCircle className="w-3 h-3 mr-1" /> Resolve
          </Button>
        )}
        {currentStatus !== "closed" && (
          <Button size="sm" variant="outline" className="border-slate-200 text-slate-600" onClick={() => updateStatus("closed")} disabled={busy} data-testid="mark-closed-btn">
            <X className="w-3 h-3 mr-1" /> Close
          </Button>
        )}
        {(currentStatus === "resolved" || currentStatus === "closed") && (
          <Button size="sm" variant="outline" onClick={() => updateStatus("pending")} disabled={busy} className="col-span-3" data-testid="reopen-btn">
            <RefreshCw className="w-3 h-3 mr-1" /> Reopen ticket
          </Button>
        )}
      </div>
    </div>
  );
}

// Inspections Tab
function CustomsInspectionTab({ token }) {
  const [mode, setMode] = useState("create"); // create, list, view
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  
  // Form state
  const [pfNumber, setPfNumber] = useState("");
  const [containerNumber, setContainerNumber] = useState("");
  const [inspectionType, setInspectionType] = useState("physical_check");
  const [notes, setNotes] = useState("");
  const [location, setLocation] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [customerEmail, setCustomerEmail] = useState("");
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Assigned inspections (pushed from the Schedule Inspection flow)
  const [assignedOrders, setAssignedOrders] = useState([]);
  const [activeOrderId, setActiveOrderId] = useState(null);
  const [inspectionResult, setInspectionResult] = useState("");
  // Photo size choice (iOS-style): "small" re-encodes/resizes to keep the
  // upload light; "original" keeps the full-resolution photo.
  const [photoQuality, setPhotoQuality] = useState("small");

  useEffect(() => {
    fetchAssigned();
  }, []);

  const fetchAssigned = async () => {
    try {
      const r = await axios.get(`${API_URL}/inspections/my-assigned`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssignedOrders(r.data.inspections || []);
    } catch (e) {
      /* no assigned inspections */
    }
  };

  const startReportForOrder = (order) => {
    setActiveOrderId(order.id);
    setPfNumber(order.pf_number || "");
    setContainerNumber(order.container_number || "");
    setLocation(order.location || "");
    setInspectionType(order.inspection_type || "physical_check");
    setInspectionResult("");
    setNotes("");
    setPhotos([]);
    setSelectedInspection(null);
    setMode("create");
    toast.success(`Filing report for ${order.pf_number || order.container_number}`);
  };

  // Resize + re-encode photos client-side so a multi-photo upload stays well
  // under the proxy body-size limit (the root cause of "Failed to create").
  // NOTE: resolves a Blob (NOT a File) — the File constructor throws on iOS
  // Safari, so we keep the filename separately and pass it to FormData.append.
  const compressImage = (file, maxDim = 1600, quality = 0.7) =>
    new Promise((resolve) => {
      try {
        if (!file.type || !file.type.startsWith("image/")) return resolve(file);
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          try {
            URL.revokeObjectURL(url);
            let { width, height } = img;
            if (!width || !height) return resolve(file);
            if (width > maxDim || height > maxDim) {
              const scale = Math.min(maxDim / width, maxDim / height);
              width = Math.round(width * scale);
              height = Math.round(height * scale);
            }
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            canvas.getContext("2d").drawImage(img, 0, 0, width, height);
            canvas.toBlob(
              (blob) => resolve(blob && blob.size ? blob : file),
              "image/jpeg",
              quality
            );
          } catch (e) {
            resolve(file);
          }
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(file);
        };
        img.src = url;
      } catch (e) {
        resolve(file);
      }
    });

  useEffect(() => {
    if (mode === "list") {
      fetchInspections();
    }
  }, [mode]);

  const fetchInspections = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/staff/mobile/customs-inspections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInspections(response.data.inspections || []);
    } catch (error) {
      toast.error("Failed to load inspections");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      let blob = file;
      let name = file.name || 'photo.jpg';
      if (photoQuality === "small") {
        blob = await compressImage(file);
        name = (file.name || 'photo').replace(/\.\w+$/, '') + '.jpg';
      }
      setPhotos((prev) => [...prev, {
        id: Date.now() + Math.random(),
        preview: URL.createObjectURL(blob),
        file: blob,
        name,
      }]);
    }
    e.target.value = '';
  };

  const removePhoto = (id) => {
    setPhotos(photos.filter((p) => p.id !== id));
  };

  const submitInspection = async () => {
    if (!pfNumber && !containerNumber) {
      toast.error("PF Number or Container required");
      return;
    }
    if (!notes.trim()) {
      toast.error("Please add inspection notes");
      return;
    }
    // Pass/Fail/Pending is decided when the inspector's report comes in.
    // Pending lets the customer get photos now; final result follows later.
    if (activeOrderId && !["pass", "fail", "pending"].includes(inspectionResult)) {
      toast.error("Please choose a result: Pass, Fail or Pending");
      return;
    }

    setUploading(true);
    try {
      // Assigned-order report → use the proper linked endpoint (auto-emails
      // customer, completes the order, creates the "finished" milestone).
      if (activeOrderId) {
        const fd = new FormData();
        fd.append('notes', notes);
        fd.append('result', inspectionResult);
        fd.append('location', location || "");
        photos.forEach((photo) => fd.append('files', photo.file));
        const response = await axios.post(
          `${API_URL}/inspections/${activeOrderId}/upload-report`,
          fd,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        );
        toast.success(
          `Report saved with ${response.data.photo_count} photos` +
          (response.data.customer_email_sent ? " — emailed to customer ✓" : "")
        );
        setActiveOrderId(null);
        setPfNumber(""); setContainerNumber(""); setNotes(""); setLocation(""); setPhotos([]);
        fetchAssigned();
        setSelectedInspection({
          id: response.data.report_id,
          view_token: response.data.view_token,
          pf_number: pfNumber,
          photo_count: response.data.photo_count,
        });
        setMode("view");
        return;
      }

      const formData = new FormData();
      formData.append('pf_number', pfNumber);
      formData.append('container_number', containerNumber);
      formData.append('inspection_type', inspectionType);
      formData.append('notes', notes);
      formData.append('location', location);
      
      photos.forEach((photo) => {
        formData.append('files', photo.file);
      });

      const response = await axios.post(
        `${API_URL}/staff/mobile/customs-inspection`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      toast.success(`Inspection created with ${response.data.photo_count} photos`);
      
      // Reset form
      setPfNumber("");
      setContainerNumber("");
      setNotes("");
      setLocation("");
      setPhotos([]);
      
      // Show the created inspection
      setSelectedInspection({
        id: response.data.inspection_id,
        view_token: response.data.view_token,
        pf_number: pfNumber,
        photo_count: response.data.photo_count
      });
      setMode("view");
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create inspection");
    } finally {
      setUploading(false);
    }
  };

  const sendToCustomer = async () => {
    if (!selectedInspection) return;
    
    setSendingEmail(true);
    try {
      const response = await axios.post(
        `${API_URL}/staff/mobile/customs-inspection/${selectedInspection.id}/send-to-customer`,
        customerEmail ? { email_to: customerEmail } : {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setCustomerEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  // View inspection result
  if (mode === "view" && selectedInspection) {
    const viewUrl = `${window.location.origin}/inspection/${selectedInspection.id}/${selectedInspection.view_token}`;
    
    return (
      <div className="space-y-4 pb-24">
        <div className="flex items-center gap-3">
          <button onClick={() => { setMode("create"); setSelectedInspection(null); }} className="p-2 -ml-2">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div>
            <h2 className="text-lg font-bold">Inspection Created</h2>
            <p className="text-sm text-slate-500">{selectedInspection.pf_number}</p>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
          <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
          <h3 className="font-semibold text-green-800">Inspection Saved!</h3>
          <p className="text-sm text-green-700 mt-1">
            {selectedInspection.photo_count} photos uploaded
          </p>
        </div>

        {/* Share Link */}
        <div className="bg-white rounded-xl border p-4">
          <h3 className="font-semibold mb-2">Customer View Link</h3>
          <div className="bg-slate-50 p-3 rounded-lg text-sm font-mono break-all text-blue-600">
            {viewUrl}
          </div>
          <Button 
            variant="outline" 
            className="w-full mt-3"
            onClick={() => {
              navigator.clipboard.writeText(viewUrl);
              toast.success("Link copied!");
            }}
          >
            Copy Link
          </Button>
        </div>

        {/* Send to Customer */}
        <div className="bg-white rounded-xl border p-4">
          <h3 className="font-semibold mb-3">Send to Customer</h3>
          <Input
            type="email"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            placeholder="Customer email (optional)"
            className="mb-3"
          />
          <Button 
            className="w-full"
            onClick={sendToCustomer}
            disabled={sendingEmail}
            data-testid="send-inspection-email-btn"
          >
            {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Send Inspection Report
          </Button>
          <p className="text-xs text-slate-500 mt-2 text-center">
            Email will include link to view all inspection photos
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => { setMode("list"); setSelectedInspection(null); }}
          >
            View All Inspections
          </Button>
          <Button 
            className="flex-1"
            onClick={() => { setMode("create"); setSelectedInspection(null); }}
          >
            New Inspection
          </Button>
        </div>
      </div>
    );
  }

  // List view
  if (mode === "list") {
    return (
      <div className="space-y-4 pb-24">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Recent Inspections</h2>
          <Button size="sm" onClick={() => setMode("create")}>
            <Plus className="w-4 h-4 mr-1" />
            New
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : inspections.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No inspections yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {inspections.map((insp) => (
              <button
                key={insp.id}
                onClick={() => {
                  setSelectedInspection(insp);
                  setMode("view");
                }}
                className="w-full bg-white rounded-xl border p-4 text-left"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{insp.pf_number || insp.container_number}</h3>
                    <p className="text-sm text-slate-500">
                      {insp.photo_count} photos • {new Date(insp.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {insp.sent_to_customer && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Sent</span>
                    )}
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Create inspection form
  const INSPECTION_TYPE_LABELS = {
    physical_check: "Customs Physical Check",
    cargo_damage: "Cargo Damage Inspection", 
    veterinary: "Veterinary Inspection",
    phytosanitary: "Phytosanitary Inspection",
    document_check: "Document Check",
    seal_check: "Seal Check"
  };

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{INSPECTION_TYPE_LABELS[inspectionType] || "Inspection"}</h2>
        <Button variant="outline" size="sm" onClick={() => setMode("list")}>
          History
        </Button>
      </div>

      {/* Assigned to me — pushed from the Schedule Inspection flow */}
      {!activeOrderId && assignedOrders.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="assigned-inspections">
          <p className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
            <ClipboardCheck className="w-4 h-4" /> Assigned to me ({assignedOrders.length})
          </p>
          <div className="space-y-2">
            {assignedOrders.map((o) => (
              <button
                key={o.id}
                onClick={() => startReportForOrder(o)}
                className="w-full bg-white rounded-lg border border-amber-200 p-3 text-left flex items-center justify-between"
                data-testid={`assigned-inspection-${o.id}`}
              >
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{o.pf_number || o.container_number}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {o.inspection_label || o.inspection_type}
                    {o.scheduled_date ? ` • ${new Date(o.scheduled_date).toLocaleString()}` : ""}
                  </p>
                </div>
                <span className="text-xs font-medium text-amber-700 flex items-center gap-1 flex-shrink-0">
                  File report <ChevronRight className="w-4 h-4" />
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Active assigned-order report banner */}
      {activeOrderId && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center justify-between" data-testid="active-order-banner">
          <p className="text-sm text-blue-800">
            Filing report for <strong>{pfNumber || containerNumber}</strong>
          </p>
          <button
            onClick={() => { setActiveOrderId(null); setPfNumber(""); setContainerNumber(""); setNotes(""); setPhotos([]); }}
            className="text-xs text-blue-600 underline"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Inspection result (for assigned-order reports) */}
      {activeOrderId && (
        <div className="bg-white rounded-xl border p-4">
          <label className="text-sm text-slate-600 mb-2 block">
            Result <span className="text-red-500">*</span>
            <span className="block text-xs text-slate-400 mt-0.5">Pending = send photos to the customer now, file the final Pass/Fail later (link stays the same)</span>
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: "pass", label: "Pass ✓", color: "#16a34a" },
              { id: "pending", label: "Pending ⏳", color: "#d97706" },
              { id: "fail", label: "Fail ✗", color: "#dc2626" },
            ].map((r) => (
              <button
                key={r.id}
                onClick={() => setInspectionResult(r.id)}
                className={`py-2 px-2 rounded-lg text-sm font-medium border-2 ${
                  inspectionResult === r.id ? "text-white" : "bg-white text-slate-700 border-slate-200"
                }`}
                style={{
                  backgroundColor: inspectionResult === r.id ? r.color : undefined,
                  borderColor: inspectionResult === r.id ? r.color : undefined,
                }}
                data-testid={`inspection-result-${r.id}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Reference */}
      <div className="bg-white rounded-xl border p-4 space-y-3">
        <div>
          <label className="text-sm text-slate-600 mb-1 block">PF Number</label>
          <Input
            value={pfNumber}
            onChange={(e) => setPfNumber(e.target.value.toUpperCase())}
            placeholder="e.g., PF-240001-01"
            data-testid="inspect-pf-input"
          />
        </div>
        <div>
          <label className="text-sm text-slate-600 mb-1 block">Container (if different)</label>
          <Input
            value={containerNumber}
            onChange={(e) => setContainerNumber(e.target.value.toUpperCase())}
            placeholder="e.g., CMAU8473513"
          />
        </div>
      </div>

      {/* Inspection Type */}
      <div className="bg-white rounded-xl border p-4">
        <label className="text-sm text-slate-600 mb-2 block">Inspection Type</label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { id: "physical_check", label: "Customs Physical", color: "#dc2626" },
            { id: "cargo_damage", label: "Cargo Damage", color: "#ea580c" },
            { id: "veterinary", label: "Veterinary", color: "#16a34a" },
            { id: "phytosanitary", label: "Phytosanitary", color: "#65a30d" },
            { id: "document_check", label: "Documents", color: "#2563eb" },
            { id: "seal_check", label: "Seal Check", color: "#7c3aed" },
          ].map((type) => (
            <button
              key={type.id}
              onClick={() => setInspectionType(type.id)}
              className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors border-2 ${
                inspectionType === type.id
                  ? "text-white"
                  : "bg-white text-slate-700 border-slate-200"
              }`}
              style={{
                backgroundColor: inspectionType === type.id ? type.color : undefined,
                borderColor: inspectionType === type.id ? type.color : undefined
              }}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white rounded-xl border p-4">
        <label className="text-sm text-slate-600 mb-1 block">Notes *</label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Describe what was checked..."
          rows={3}
          data-testid="inspect-notes-input"
        />
      </div>

      {/* Location */}
      <div className="bg-white rounded-xl border p-4">
        <label className="text-sm text-slate-600 mb-1 block">Location</label>
        <Input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="e.g., Port of Koper, Terminal C"
        />
      </div>

      {/* Photos */}
      <div className="bg-white rounded-xl border p-4">
        <label className="text-sm text-slate-600 mb-2 block">
          Photos ({photos.length})
        </label>

        {/* iOS-style size choice — decide before picking photos */}
        <div className="mb-3">
          <p className="text-xs text-slate-500 mb-1">Photo size</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { id: "small", label: "Smaller (recommended)" },
              { id: "original", label: "Original size" },
            ].map((q) => (
              <button
                key={q.id}
                onClick={() => setPhotoQuality(q.id)}
                className={`py-2 px-2 rounded-lg text-xs font-medium border-2 ${
                  photoQuality === q.id
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-slate-700 border-slate-200"
                }`}
                data-testid={`photo-quality-${q.id}`}
              >
                {q.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            {photoQuality === "small"
              ? "Photos are resized & compressed for faster upload."
              : "Photos are uploaded at full resolution (larger files)."}
          </p>
        </div>
        
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />

        {photos.length > 0 && (
          <div className="grid grid-cols-4 gap-2 mb-3">
            {photos.map((photo) => (
              <div key={photo.id} className="relative aspect-square">
                <img
                  src={photo.preview}
                  alt={photo.name}
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  onClick={() => removePhoto(photo.id)}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => cameraInputRef.current?.click()}
            data-testid="inspect-camera-btn"
          >
            <Camera className="w-4 h-4 mr-1" />
            Camera
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => fileInputRef.current?.click()}
          >
            <Image className="w-4 h-4 mr-1" />
            Gallery
          </Button>
        </div>
      </div>

      {/* Submit */}
      <Button
        className="w-full"
        onClick={submitInspection}
        disabled={uploading || (!pfNumber && !containerNumber) || !notes.trim() || (activeOrderId && !["pass", "fail", "pending"].includes(inspectionResult))}
        data-testid="submit-inspection-btn"
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
        ) : (
          <ClipboardCheck className="w-4 h-4 mr-2" />
        )}
        Save Inspection
      </Button>
    </div>
  );
}

// Driver Location Block — 2026-05-16
// Shows a leaflet map with the latest driver pings + a CTA button to request
// a fresh GPS update from the driver via Viber/WhatsApp.
function DriverLocationBlock({ order, pings, loadingPings, pinging, onPing, onRefresh }) {
  // Latest ping with a real lat/lon
  const pingsWithLocation = (pings || []).filter(
    (p) => p?.location && p.location.lat != null && p.location.lon != null
  );
  const sortedLocated = [...pingsWithLocation].sort((a, b) => {
    const ta = new Date(a.last_update || a.responded_at || a.requested_at || 0).getTime();
    const tb = new Date(b.last_update || b.responded_at || b.requested_at || 0).getTime();
    return tb - ta;
  });
  const latest = sortedLocated[0];
  const pendingPing = (pings || []).find((p) => p.status === "pending");

  // Build a marker icon (default leaflet icons need this fix in webpack)
  const truckIcon = L.divIcon({
    className: "",
    html: `<div style="background:#7c3aed;color:#fff;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);font-size:16px;">🚚</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
  const pastIcon = L.divIcon({
    className: "",
    html: `<div style="background:#94a3b8;color:#fff;width:18px;height:18px;border-radius:50%;border:2px solid #fff;"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });

  const formatTime = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-GB", {
        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  return (
    <div className="bg-white rounded-xl border p-4 space-y-3" data-testid="driver-location-block">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <MapPin className="w-4 h-4 text-purple-600" />
          Driver Location
        </h3>
        <Button
          size="sm"
          variant="ghost"
          onClick={onRefresh}
          disabled={loadingPings}
          data-testid="refresh-pings-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loadingPings ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Request location CTA */}
      <Button
        size="sm"
        onClick={onPing}
        disabled={pinging || !order?.driver_phone}
        className="w-full bg-purple-600 hover:bg-purple-700"
        data-testid="request-driver-location-btn"
      >
        {pinging ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending request…</>
        ) : (
          <><MapPin className="w-4 h-4 mr-2" /> Request current location from driver</>
        )}
      </Button>

      {!order?.driver_phone && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
          ⚠️ Add a driver phone number to the TO before requesting location.
        </p>
      )}

      {pendingPing && (
        <div className="text-xs bg-blue-50 border border-blue-200 rounded-md p-2 text-blue-800" data-testid="pending-ping-info">
          <Clock className="w-3 h-3 inline mr-1" />
          Awaiting driver response — request sent {formatTime(pendingPing.requested_at)}
        </div>
      )}

      {/* Latest reading summary */}
      {latest && (
        <div className="bg-purple-50 border border-purple-200 rounded-md p-3 text-sm" data-testid="latest-ping-summary">
          <div className="flex items-center justify-between">
            <span className="font-medium text-purple-800">Last reported</span>
            <span className="text-xs text-purple-600">
              {formatTime(latest.last_update || latest.responded_at)}
            </span>
          </div>
          <div className="text-slate-700 mt-1 text-xs">
            {latest.location?.address || `${latest.location.lat.toFixed(4)}, ${latest.location.lon.toFixed(4)}`}
          </div>
          {latest.update_count > 1 && (
            <div className="text-[10px] text-purple-500 mt-1">
              {latest.update_count} updates received from this link
            </div>
          )}
        </div>
      )}

      {/* Map */}
      {sortedLocated.length > 0 ? (
        <div className="rounded-lg overflow-hidden border" style={{ height: 240 }} data-testid="driver-location-map">
          <MapContainer
            center={[latest.location.lat, latest.location.lon]}
            zoom={11}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom={false}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {/* Latest position — purple truck icon */}
            <Marker position={[latest.location.lat, latest.location.lon]} icon={truckIcon}>
              <Popup>
                <strong>Driver position</strong><br />
                {latest.location.address || `${latest.location.lat.toFixed(4)}, ${latest.location.lon.toFixed(4)}`}<br />
                <small>{formatTime(latest.last_update || latest.responded_at)}</small>
              </Popup>
            </Marker>
            {/* Historical pings — small grey dots */}
            {sortedLocated.slice(1, 10).map((p, i) => (
              <Marker
                key={p.token || i}
                position={[p.location.lat, p.location.lon]}
                icon={pastIcon}
              >
                <Popup>
                  <small>{formatTime(p.last_update || p.responded_at)}</small><br />
                  {p.location.address || `${p.location.lat.toFixed(4)}, ${p.location.lon.toFixed(4)}`}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      ) : (
        <p className="text-xs text-slate-500 text-center py-4 bg-slate-50 rounded-md">
          {loadingPings ? "Loading location history…" :
           pings && pings.length > 0
             ? "Driver has not shared GPS yet — location request still pending."
             : "No location pings yet. Tap the button above to request one."}
        </p>
      )}

      {/* Recent pings timeline */}
      {pings && pings.length > 0 && (
        <details className="text-xs text-slate-600">
          <summary className="cursor-pointer font-medium py-1">
            History ({pings.length} request{pings.length === 1 ? "" : "s"})
          </summary>
          <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
            {[...pings].reverse().slice(0, 10).map((p, i) => (
              <div key={p.token || i} className="flex items-center justify-between text-[11px] py-1 border-b last:border-b-0">
                <span>{formatTime(p.requested_at)}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                  p.status === "received" || p.status === "active"
                    ? "bg-emerald-100 text-emerald-700"
                    : p.status === "pending"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-slate-100 text-slate-600"
                }`}>
                  {p.status || "—"}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}


// Transport Orders Tab — 2026-05-15 (mobile parity with web)
function TransportOrdersTab({ token, pendingOpenTo, clearPendingOpenTo, onJumpToPf }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [linkValidDays, setLinkValidDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [linkInfo, setLinkInfo] = useState(null);
  // 2026-05-16: driver-location pings
  const [pings, setPings] = useState([]);
  const [loadingPings, setLoadingPings] = useState(false);
  const [pinging, setPinging] = useState(false);
  // 2026-05-17 — Transport Actions (moved here from PF detail).
  // Msg Dispatcher / Msg Driver / Ping Driver GPS now belong with the TO,
  // since the driver/dispatcher/carrier are TO attributes, not PF.
  // 2026-05-22 — upgraded to mirror the desktop "Send Message" tab:
  // channel picker (Email/WhatsApp/SMS) + quick templates + email subject.
  const [showTransportMessageModal, setShowTransportMessageModal] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState("");
  const [transportMessage, setTransportMessage] = useState("");
  const [transportChannel, setTransportChannel] = useState("whatsapp"); // email | whatsapp | sms
  const [transportSubject, setTransportSubject] = useState("");
  const [sendingTransportMsg, setSendingTransportMsg] = useState(false);
  const [pingingDriver, setPingingDriver] = useState(false);
  // 2026-05-16 — sort (mobile parity with desktop /transport view).
  // Default = "created_at" desc (the user explicitly asked: main sort
  // is created date, NOT order number — old text-prefixed numbers like
  // "EPACTR-PF-zilnia-2017-169" were sorting on top lexicographically).
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  // 2026-05-16 — desktop-parity filters (date range, price range,
  // container #, trucking company). Sheet is hidden by default; active
  // filter count is shown as a badge on the toggle button.
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    price_min: "",
    price_max: "",
    container_number: "",
    trucking_company: "",
  });
  // Distinct carriers list for the autocomplete datalist
  const [carriers, setCarriers] = useState([]);

  useEffect(() => { fetchOrders(); /* eslint-disable-next-line */ }, [sortBy, sortOrder, filters]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        per_page: "100",
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) params.set(k, v);
      });
      const r = await axios.get(
        `${API_URL}/transport-orders?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOrders(r.data.orders || r.data || []);

      // Refresh the carrier autocomplete list (only when no filter is on,
      // so we keep the FULL set of carriers for typeahead even after
      // narrowing the result set).
      const noFilters = Object.values(filters).every((v) => !v);
      if (noFilters) {
        const seen = new Set();
        (r.data.orders || []).forEach((o) => {
          const name = o.trucking_company_name || o.trucking_company?.name || "";
          if (name) seen.add(name);
        });
        setCarriers(Array.from(seen).sort());
      }
    } catch (e) {
      toast.error("Failed to load transport orders");
    } finally {
      setLoading(false);
    }
  };

  const activeFilterCount = Object.values(filters).filter((v) => v !== "").length;
  const resetFilters = () => setFilters({
    date_from: "", date_to: "", price_min: "", price_max: "",
    container_number: "", trucking_company: "",
  });
  const updateFilter = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));

  // Click a sort field: if it's the active one, flip direction; otherwise switch field and default to desc
  const toggleSort = (field) => {
    if (field === sortBy) {
      setSortOrder(sortOrder === "desc" ? "asc" : "desc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const filtered = orders.filter((o) => {
    const status = (o.workflow_status || "").toLowerCase();
    if (statusFilter === "active" && (status === "completed" || status === "pod_received")) return false;
    if (statusFilter === "pod_needed" && status !== "delivered" && status !== "dispatched" && status !== "loaded") return false;
    if (statusFilter === "completed" && status !== "completed" && status !== "pod_received") return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (o.order_number || "").toLowerCase().includes(q) ||
      (o.pf_number || "").toLowerCase().includes(q) ||
      (o.trucking_company_name || o.trucking_company?.name || "").toLowerCase().includes(q) ||
      (o.unloading_place || "").toLowerCase().includes(q)
    );
  });

  const generateLink = async (order) => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API_URL}/transport-orders/${order.id}/generate-tracking-link?valid_days=${linkValidDays}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLinkInfo({ url: r.data.tracking_url, expires_at: r.data.expires_at });
      toast.success("Tracking link generated");
      fetchOrders();
    } catch (e) {
      toast.error("Failed to generate link: " + (e.response?.data?.detail || ""));
    } finally {
      setBusy(false);
    }
  };

  const revokeLink = async (order) => {
    if (!window.confirm("Revoke the customer tracking link now? Customer will get 410 Gone immediately.")) return;
    setBusy(true);
    try {
      await axios.post(
        `${API_URL}/transport-orders/${order.id}/revoke-tracking-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLinkInfo(null);
      toast.success("Link revoked");
      fetchOrders();
    } catch (e) {
      toast.error("Failed to revoke");
    } finally {
      setBusy(false);
    }
  };

  const deletePod = async (order) => {
    if (!window.confirm("Delete POD? TO status will roll back to dispatched.")) return;
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/transport-orders/${order.id}/pod`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("POD deleted");
      fetchOrders();
      setSelectedOrder(null);
    } catch (e) {
      toast.error("Failed to delete POD");
    } finally {
      setBusy(false);
    }
  };

  const reactivateOrder = async (order) => {
    if (!window.confirm("Reactivate this closed TO?")) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/transport-orders/${order.id}/reactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("TO reactivated");
      fetchOrders();
      setSelectedOrder(null);
    } catch (e) {
      toast.error("Failed to reactivate");
    } finally {
      setBusy(false);
    }
  };

  // --- 2026-05-16: Driver location ping helpers ---
  const fetchPings = async (order) => {
    if (!order?.id) return;
    setLoadingPings(true);
    try {
      const r = await axios.get(
        `${API_URL}/transport-orders/${order.id}/tracking-history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPings(r.data?.pings || []);
    } catch (e) {
      // Silent — pings are optional
      setPings([]);
    } finally {
      setLoadingPings(false);
    }
  };

  const sendLocationPing = async (order) => {
    if (!order?.driver_phone) {
      toast.error("Driver phone number missing — set it on the TO first");
      return;
    }
    if (!window.confirm(
      `Send a location request to driver ${order.driver_name || order.driver_phone}?\n\n` +
      `They will receive a Viber/WhatsApp link asking for their current GPS position. ` +
      `Location sharing is voluntary (GDPR).`
    )) return;
    setPinging(true);
    try {
      const r = await axios.post(
        `${API_URL}/transport-orders/${order.id}/send-tracking-ping`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(r.data?.message || "Location request sent to driver");
      // Refresh pings list to show the new pending one
      await fetchPings(order);
    } catch (e) {
      toast.error("Failed to send ping: " + (e.response?.data?.detail || e.message));
    } finally {
      setPinging(false);
    }
  };

  // Open detail → fetch pings
  useEffect(() => {
    if (selectedOrder) {
      fetchPings(selectedOrder);
    } else {
      setPings([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrder?.id]);

  // 2026-05-17 — Deep-link from PF: when a TO is requested by another
  // tab, fetch (if needed) and open it as the selectedOrder.
  useEffect(() => {
    if (!pendingOpenTo) return;
    let cancelled = false;
    const openIt = async () => {
      try {
        // Caller may pass a full TO object OR an id/order_number string.
        if (typeof pendingOpenTo === "object" && pendingOpenTo.id) {
          if (!cancelled) setSelectedOrder(pendingOpenTo);
        } else {
          const idOrNumber = typeof pendingOpenTo === "string"
            ? pendingOpenTo
            : (pendingOpenTo.id || pendingOpenTo.order_number);
          const r = await axios.get(`${API_URL}/transport-orders/${idOrNumber}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!cancelled && r.data) setSelectedOrder(r.data);
        }
      } catch (e) {
        toast.error("Failed to open the Transport Order");
      } finally {
        if (clearPendingOpenTo) clearPendingOpenTo();
      }
    };
    openIt();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpenTo]);

  // 2026-05-17 — Transport Actions handlers (moved from PF detail).
  // 2026-05-22 — uses the new send-custom-message endpoint so the same
  // form can route Email / WhatsApp / SMS, with quick templates and
  // an audit trail in `order.email_history` (mirrors desktop UX).
  const TO_QUICK_TEMPLATES = [
    { id: "loc",      label: "Request current location",  subject: "Location update needed",
      body: (ord) => `Hi,\n\nCould you please share the current location & ETA for order ${ord?.order_number || ""}?\n\nThanks,\nEuropacific Ops` },
    { id: "loading", label: "Confirm loading ETA",        subject: "Loading ETA confirmation",
      body: (ord) => `Hi,\n\nPlease confirm the truck's ETA at the loading address for order ${ord?.order_number || ""}.\n\nThanks,\nEuropacific Ops` },
    { id: "delay",   label: "Ask about delay",            subject: "Delay update",
      body: (ord) => `Hi,\n\nWe haven't received an update on order ${ord?.order_number || ""}. Could you let us know the current status and any expected delays?\n\nThanks,\nEuropacific Ops` },
    { id: "deliv",   label: "Confirm delivery / unload",  subject: "Delivery confirmation needed",
      body: (ord) => `Hi,\n\nHas the cargo for order ${ord?.order_number || ""} been delivered? If yes, please share the unloading time and any remarks.\n\nThanks,\nEuropacific Ops` },
    { id: "pod",     label: "Request signed POD",         subject: "POD requested",
      body: (ord) => `Hi,\n\nPlease send us the signed POD for order ${ord?.order_number || ""} at your earliest convenience.\n\nThanks,\nEuropacific Ops` },
  ];

  const applyTransportTemplate = (tid) => {
    const tpl = TO_QUICK_TEMPLATES.find(t => t.id === tid);
    if (!tpl) return;
    setTransportSubject(tpl.subject);
    setTransportMessage(tpl.body(selectedOrder));
  };

  // Compute the live target address based on recipient + channel selection.
  const resolveTransportTarget = () => {
    if (!selectedOrder) return "—";
    if (messageRecipient === "dispatcher") {
      const tc = selectedOrder.trucking_company || {};
      return transportChannel === "email"
        ? (tc.email || tc.dispatcher_email || "—")
        : (tc.dispatcher_tel || tc.phone || tc.dispatcher_phone || "—");
    }
    return transportChannel === "email"
      ? (selectedOrder.driver_email || "—")
      : (selectedOrder.driver_phone || "—");
  };

  const sendTransportMessage = async () => {
    if (!transportMessage.trim() || !selectedOrder) return;
    const target = resolveTransportTarget();
    if (!target || target === "—") {
      toast.error(
        `No ${messageRecipient} ${transportChannel === "email" ? "email" : "phone"} on this order`
      );
      return;
    }
    setSendingTransportMsg(true);
    try {
      const fd = new FormData();
      fd.append("channel", transportChannel);
      fd.append("recipient_kind", messageRecipient);
      if (transportChannel === "email") fd.append("subject", transportSubject || "");
      fd.append("message", transportMessage);
      await axios.post(
        `${API_URL}/transport-orders/${selectedOrder.id}/send-custom-message`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } },
      );
      toast.success(`${transportChannel.toUpperCase()} sent to ${messageRecipient}`);
      setTransportMessage("");
      setTransportSubject("");
      setShowTransportMessageModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to send ${transportChannel}`);
    } finally {
      setSendingTransportMsg(false);
    }
  };

  // Lightweight GPS ping — records a pending request the driver app can answer.
  // (Different from `sendLocationPing` above, which opens a Viber/WhatsApp link.)
  const pingDriverLocation = async () => {
    if (!selectedOrder) return;
    setPingingDriver(true);
    try {
      const r = await axios.post(
        `${API_URL}/transport-orders/${selectedOrder.id}/ping-driver`,
        { pf_number: selectedOrder.pf_number },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (r.data?.location) {
        toast.success(`Driver location: ${r.data.location}`);
      } else {
        toast.info("GPS request sent to driver. Location will update shortly.");
      }
    } catch (e) {
      toast.error("Failed to ping driver location");
    } finally {
      setPingingDriver(false);
    }
  };

  // --- Share helpers (2026-05-15) ---
  const buildTrackingUrl = (order) => {
    if (linkInfo?.url) return linkInfo.url;
    const token = order.customer_tracking_token;
    if (!token) return null;
    return `https://myeuropacific.com/tracking/${order.id}/${token}`;
  };

  const buildShareText = (order) => {
    const url = buildTrackingUrl(order);
    if (!url) return null;
    const dest = order.unloading_place?.split("\n")[0] || order.destination || "delivery";
    return `Your shipment to ${dest} (${order.order_number}) is on its way.\n\nLive tracking: ${url}`;
  };

  const shareViaWhatsApp = (order) => {
    const text = buildShareText(order);
    if (!text) {
      toast.error("Generate a tracking link first");
      return;
    }
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const shareNative = async (order) => {
    const text = buildShareText(order);
    const url = buildTrackingUrl(order);
    if (!text || !url) {
      toast.error("Generate a tracking link first");
      return;
    }
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Shipment ${order.order_number}`,
          text,
          url,
        });
      } catch (e) {
        // User cancelled or share failed — fall back to clipboard
        if (e.name !== "AbortError") {
          navigator.clipboard.writeText(text);
          toast.success("Copied to clipboard");
        }
      }
    } else {
      navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard (Share API not available)");
    }
  };

  const shareViaEmail = async (order) => {
    if (!order.customer_email) {
      // Fallback: open the user's mail client with mailto:
      const text = buildShareText(order);
      if (!text) {
        toast.error("Generate a tracking link first");
        return;
      }
      const subject = `Shipment Update: ${order.pf_number || order.order_number}`;
      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API_URL}/transport-orders/${order.id}/generate-customer-email`,
        { include_tracking_link: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Update email sent to ${order.customer_email}`);
    } catch (e) {
      toast.error("Failed to send email: " + (e.response?.data?.detail || ""));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  const statusBadgeColor = (s) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-700",
      confirmed: "bg-blue-100 text-blue-700",
      dispatched: "bg-purple-100 text-purple-700",
      loaded: "bg-indigo-100 text-indigo-700",
      delivered: "bg-emerald-100 text-emerald-700",
      pod_received: "bg-green-100 text-green-700",
      completed: "bg-green-100 text-green-700",
    };
    return colors[s] || "bg-slate-100 text-slate-600";
  };

  // Detail view for selected TO
  if (selectedOrder) {
    return (
      <div className="space-y-4 pb-24">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => { setSelectedOrder(null); setLinkInfo(null); }} data-testid="to-back-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h2 className="font-semibold text-slate-800">{selectedOrder.order_number}</h2>
            {selectedOrder.pf_number ? (
              <button
                type="button"
                onClick={() => onJumpToPf && onJumpToPf(selectedOrder.pf_number)}
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 hover:underline"
                data-testid="to-jump-to-pf-btn"
              >
                <FolderOpen className="w-3 h-3" />
                {selectedOrder.pf_number}
              </button>
            ) : (
              <p className="text-xs text-slate-500">—</p>
            )}
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadgeColor(selectedOrder.workflow_status)}`}>
            {(selectedOrder.workflow_status || "pending").replace(/_/g, " ")}
          </span>
        </div>

        {/* Info card */}
        <div className="bg-white rounded-xl border p-4 space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-slate-400" />
            <span className="text-slate-600">{selectedOrder.trucking_company_name || selectedOrder.trucking_company?.name || "Carrier pending"}</span>
          </div>
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
            <span className="text-slate-600 flex-1">{selectedOrder.unloading_place || selectedOrder.destination || "—"}</span>
          </div>
          {selectedOrder.driver_phone && (
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-slate-400" />
              <a href={`tel:${selectedOrder.driver_phone}`} className="text-blue-600">{selectedOrder.driver_phone}</a>
            </div>
          )}
        </div>

        {/* 2026-05-17 — Transport Actions (moved from PF detail). */}
        <div className="bg-white rounded-xl p-4 border shadow-sm" data-testid="to-transport-actions">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Truck className="w-4 h-4 text-purple-600" />
            Transport Actions
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-auto py-3 flex flex-col items-center gap-1"
              onClick={() => {
                setMessageRecipient("dispatcher");
                setShowTransportMessageModal(true);
              }}
              data-testid="msg-dispatcher-btn"
            >
              <Phone className="w-4 h-4 text-blue-600" />
              <span className="text-xs">Msg Dispatcher</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-auto py-3 flex flex-col items-center gap-1"
              onClick={() => {
                setMessageRecipient("driver");
                setShowTransportMessageModal(true);
              }}
              data-testid="msg-driver-btn"
            >
              <MessageSquare className="w-4 h-4 text-green-600" />
              <span className="text-xs">Msg Driver</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-auto py-3 flex flex-col items-center gap-1 col-span-2"
              onClick={pingDriverLocation}
              disabled={pingingDriver}
              data-testid="ping-gps-btn"
            >
              {pingingDriver ? (
                <Loader2 className="w-4 h-4 animate-spin text-orange-600" />
              ) : (
                <MapPin className="w-4 h-4 text-orange-600" />
              )}
              <span className="text-xs">Ping Driver GPS</span>
            </Button>
          </div>
        </div>

        {/* Tracking Link Section */}
        <div className="bg-white rounded-xl border p-4 space-y-3" data-testid="to-tracking-link-section">
          <h3 className="font-semibold flex items-center gap-2">
            <ExternalLink className="w-4 h-4 text-blue-600" />
            Customer Tracking Link
          </h3>
          {selectedOrder.customer_tracking_token && !linkInfo ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <p className="text-blue-800 font-medium mb-1">Active link</p>
              {selectedOrder.customer_tracking_expires && (
                <p className="text-xs text-blue-700 mb-2">
                  Expires: {new Date(selectedOrder.customer_tracking_expires).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </p>
              )}
            </div>
          ) : null}
          {linkInfo && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs break-all">
              <p className="text-emerald-700 font-medium mb-1">New link ready</p>
              <p className="text-emerald-800">{linkInfo.url}</p>
              {linkInfo.expires_at && <p className="text-emerald-600 mt-1">Expires: {new Date(linkInfo.expires_at).toLocaleString("en-GB")}</p>}
              <Button size="sm" variant="outline" className="mt-2" onClick={() => copyLink(linkInfo.url)} data-testid="copy-tracking-link-btn">
                Copy
              </Button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-600">Valid for</label>
            <select
              value={linkValidDays}
              onChange={(e) => setLinkValidDays(parseInt(e.target.value))}
              className="border rounded-md text-sm p-1.5 flex-1"
              data-testid="link-valid-days-select"
            >
              {[1, 3, 7, 14, 30, 60, 90].map((d) => (
                <option key={d} value={d}>{d} day{d > 1 ? "s" : ""}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button size="sm" onClick={() => generateLink(selectedOrder)} disabled={busy} data-testid="generate-tracking-link-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              {selectedOrder.customer_tracking_token ? "Regenerate" : "Generate"}
            </Button>
            {selectedOrder.customer_tracking_token && (
              <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => revokeLink(selectedOrder)} disabled={busy} data-testid="revoke-tracking-link-btn">
                Revoke now
              </Button>
            )}
          </div>

          {/* Share buttons — visible once a link exists */}
          {(selectedOrder.customer_tracking_token || linkInfo) && (
            <div className="pt-2 border-t">
              <p className="text-xs text-slate-500 mb-2">Share with customer</p>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-auto py-3 flex flex-col items-center gap-1 border-green-200 text-green-700 hover:bg-green-50"
                  onClick={() => shareViaWhatsApp(selectedOrder)}
                  data-testid="share-whatsapp-btn"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span className="text-[11px] font-medium">WhatsApp</span>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-auto py-3 flex flex-col items-center gap-1 border-blue-200 text-blue-700 hover:bg-blue-50"
                  onClick={() => shareViaEmail(selectedOrder)}
                  disabled={busy}
                  data-testid="share-email-btn"
                >
                  <Mail className="w-4 h-4" />
                  <span className="text-[11px] font-medium">Email</span>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-auto py-3 flex flex-col items-center gap-1"
                  onClick={() => shareNative(selectedOrder)}
                  data-testid="share-native-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span className="text-[11px] font-medium">Share…</span>
                </Button>
              </div>
              {selectedOrder.customer_email && (
                <p className="text-[10px] text-slate-400 mt-2 text-center">
                  Email → {selectedOrder.customer_email}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Driver Location Pings (GPS) — 2026-05-16 */}
        <DriverLocationBlock
          order={selectedOrder}
          pings={pings}
          loadingPings={loadingPings}
          pinging={pinging}
          onPing={() => sendLocationPing(selectedOrder)}
          onRefresh={() => fetchPings(selectedOrder)}
        />

        {/* POD section */}
        <div className="bg-white rounded-xl border p-4 space-y-3">
          <h3 className="font-semibold flex items-center gap-2">
            <FileImage className="w-4 h-4 text-orange-600" />
            Proof of Delivery
          </h3>
          {selectedOrder.pod_file_id ? (
            <>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-green-700">POD on file</span>
                {selectedOrder.pod_uploaded_at && (
                  <span className="text-xs text-green-600 ml-auto">
                    {new Date(selectedOrder.pod_uploaded_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                  </span>
                )}
              </div>
              <Button size="sm" variant="outline" className="w-full text-red-600 border-red-200" onClick={() => deletePod(selectedOrder)} disabled={busy} data-testid="delete-pod-btn">
                <Trash2 className="w-4 h-4 mr-1" />
                Delete POD
              </Button>
            </>
          ) : (
            <p className="text-sm text-slate-500">No POD uploaded yet. Driver can send via WhatsApp or use the POD upload tab.</p>
          )}
        </div>

        {/* Reactivate */}
        {(selectedOrder.workflow_status === "completed" || selectedOrder.workflow_status === "pod_received") && (
          <Button size="sm" variant="outline" className="w-full" onClick={() => reactivateOrder(selectedOrder)} disabled={busy} data-testid="reactivate-to-btn">
            <RefreshCw className="w-4 h-4 mr-1" />
            Reopen TO
          </Button>
        )}

        {/* 2026-05-17 — Transport Message Modal (moved from PF detail). */}
        {showTransportMessageModal && (
          <div className="fixed inset-0 bg-black/50 flex items-end z-50" data-testid="to-transport-msg-modal">
            <div className="bg-white w-full rounded-t-2xl p-4 pb-8 animate-slide-up max-h-[92vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2">
                  {messageRecipient === "dispatcher" ? (
                    <Phone className="w-4 h-4 text-blue-600" />
                  ) : (
                    <MessageSquare className="w-4 h-4 text-green-600" />
                  )}
                  Message {messageRecipient === "dispatcher" ? "Dispatcher" : "Driver"}
                </h3>
                <Button size="sm" variant="ghost" onClick={() => setShowTransportMessageModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Re: <span className="font-mono">{selectedOrder?.order_number}</span>
                {selectedOrder?.pf_number ? <> — <span className="font-mono">{selectedOrder.pf_number}</span></> : null}
                {selectedOrder?.trucking_company_name ? ` • ${selectedOrder.trucking_company_name}` : ""}
              </p>

              {/* Channel picker */}
              <div className="mb-3">
                <div className="text-xs font-medium text-slate-600 mb-1">Channel</div>
                <div className="grid grid-cols-3 gap-1">
                  {[
                    { id: "whatsapp", label: "WhatsApp" },
                    { id: "email", label: "Email" },
                    { id: "sms", label: "SMS" },
                  ].map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setTransportChannel(c.id)}
                      className={`px-2 py-2 text-xs font-medium rounded border transition-colors ${
                        transportChannel === c.id
                          ? "border-purple-500 bg-purple-50 text-purple-700"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                      data-testid={`mob-to-channel-${c.id}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Resolved target chip */}
              <div className="mb-3 px-3 py-2 rounded bg-slate-100 text-xs flex items-center justify-between" data-testid="mob-to-target">
                <div className="text-slate-500">Sending to:</div>
                <div className="font-mono font-semibold text-slate-800">{resolveTransportTarget()}</div>
              </div>

              {/* Templates */}
              <div className="mb-3">
                <div className="text-xs font-medium text-slate-600 mb-1">Quick template</div>
                <select
                  onChange={(e) => { if (e.target.value) applyTransportTemplate(e.target.value); }}
                  defaultValue=""
                  className="w-full px-3 py-2 text-sm rounded border border-slate-200 bg-white"
                  data-testid="mob-to-template"
                >
                  <option value="">— pick a template —</option>
                  {TO_QUICK_TEMPLATES.map(t => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>

              {/* Subject (email only) */}
              {transportChannel === "email" && (
                <div className="mb-3">
                  <div className="text-xs font-medium text-slate-600 mb-1">Subject</div>
                  <input
                    type="text"
                    value={transportSubject}
                    onChange={(e) => setTransportSubject(e.target.value)}
                    placeholder="(auto-prefixed with order number)"
                    className="w-full px-3 py-2 text-sm rounded border border-slate-200"
                    data-testid="mob-to-subject"
                  />
                </div>
              )}

              <Textarea
                placeholder={transportChannel === "email"
                  ? "Type your email body…"
                  : (messageRecipient === "dispatcher"
                      ? "Message to dispatcher (e.g., 'Need urgent pickup rescheduled')"
                      : "Message to driver (e.g., 'Call customer before delivery')")}
                value={transportMessage}
                onChange={(e) => setTransportMessage(e.target.value)}
                rows={5}
                className="mb-3 font-mono text-sm"
                data-testid="transport-message-input"
              />
              {transportChannel !== "email" && (
                <div className="text-xs text-slate-400 -mt-2 mb-3">
                  {transportMessage.length} characters · {transportChannel === "whatsapp" ? "WhatsApp" : "SMS"} message
                </div>
              )}

              <Button
                className="w-full bg-purple-600 hover:bg-purple-700"
                onClick={sendTransportMessage}
                disabled={sendingTransportMsg || !transportMessage.trim()}
                data-testid="send-transport-msg-btn"
              >
                {sendingTransportMsg ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send {transportChannel === "email" ? "Email" : transportChannel === "whatsapp" ? "WhatsApp" : "SMS"}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Truck className="w-5 h-5 text-purple-600" />
          Transport Orders
        </h2>
        <Button size="sm" variant="ghost" onClick={fetchOrders} disabled={loading} data-testid="to-refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Search */}
      <div className="relative flex gap-2 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search TO# / PF / carrier / destination..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="to-search-input"
          />
        </div>
        <button
          onClick={() => setShowFilters(true)}
          className={`relative flex-shrink-0 h-10 w-10 rounded-lg border flex items-center justify-center ${
            activeFilterCount > 0
              ? "bg-purple-600 text-white border-purple-600"
              : "bg-white text-slate-600 border-slate-200"
          }`}
          data-testid="to-filter-toggle"
          aria-label="Filters"
        >
          <Filter className="w-4 h-4" />
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-amber-400 text-slate-900 text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* 2026-05-16 — Filter sheet (slide-up) — desktop-parity advanced
          filters: date range, price range, container#, carrier. */}
      {showFilters && (
        <div
          className="fixed inset-0 z-[9999] bg-black/40 flex items-end"
          onClick={() => setShowFilters(false)}
          data-testid="to-filter-sheet-backdrop"
        >
          <div
            className="w-full bg-white rounded-t-2xl p-4 max-h-[85vh] overflow-y-auto shadow-2xl animate-slide-up-sheet"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
            data-testid="to-filter-sheet"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold flex items-center gap-2">
                <Filter className="w-4 h-4 text-purple-600" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="text-xs bg-purple-100 text-purple-700 rounded-full px-2 py-0.5">
                    {activeFilterCount} active
                  </span>
                )}
              </h3>
              <button
                onClick={() => setShowFilters(false)}
                className="p-1 hover:bg-slate-100 rounded"
                aria-label="Close filters"
                data-testid="to-filter-close"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Date range — loading date */}
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Loading date range</label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => updateFilter("date_from", e.target.value)}
                    data-testid="to-filter-date-from"
                  />
                  <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => updateFilter("date_to", e.target.value)}
                    data-testid="to-filter-date-to"
                  />
                </div>
              </div>

              {/* Price range */}
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Price (EUR)</label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={filters.price_min}
                    onChange={(e) => updateFilter("price_min", e.target.value)}
                    data-testid="to-filter-price-min"
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={filters.price_max}
                    onChange={(e) => updateFilter("price_max", e.target.value)}
                    data-testid="to-filter-price-max"
                  />
                </div>
              </div>

              {/* Container number */}
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Container #</label>
                <Input
                  placeholder="e.g. MSCU1234567"
                  value={filters.container_number}
                  onChange={(e) => updateFilter("container_number", e.target.value.toUpperCase())}
                  data-testid="to-filter-container"
                />
              </div>

              {/* Trucking company — autocomplete via datalist */}
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Trucking company</label>
                <Input
                  list="to-carriers-datalist"
                  placeholder="Type to search…"
                  value={filters.trucking_company}
                  onChange={(e) => updateFilter("trucking_company", e.target.value)}
                  data-testid="to-filter-carrier"
                />
                <datalist id="to-carriers-datalist">
                  {carriers.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>

              {/* Quick presets */}
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Quick presets</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      const today = new Date();
                      const nextWeek = new Date();
                      nextWeek.setDate(today.getDate() + 7);
                      updateFilter("date_from", today.toISOString().slice(0, 10));
                      updateFilter("date_to", nextWeek.toISOString().slice(0, 10));
                    }}
                    className="px-3 py-1.5 text-xs rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
                    data-testid="to-filter-preset-next7"
                  >
                    Next 7 days
                  </button>
                  <button
                    onClick={() => {
                      const today = new Date();
                      const lastWeek = new Date();
                      lastWeek.setDate(today.getDate() - 7);
                      updateFilter("date_from", lastWeek.toISOString().slice(0, 10));
                      updateFilter("date_to", today.toISOString().slice(0, 10));
                    }}
                    className="px-3 py-1.5 text-xs rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
                    data-testid="to-filter-preset-last7"
                  >
                    Last 7 days
                  </button>
                  <button
                    onClick={() => updateFilter("price_min", "500")}
                    className="px-3 py-1.5 text-xs rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
                    data-testid="to-filter-preset-over500"
                  >
                    Over €500
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-5 pt-3 border-t">
              <Button
                variant="outline"
                className="flex-1"
                onClick={resetFilters}
                disabled={activeFilterCount === 0}
                data-testid="to-filter-reset"
              >
                Reset
              </Button>
              <Button
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                onClick={() => setShowFilters(false)}
                data-testid="to-filter-apply"
              >
                Show {orders.length} results
              </Button>
            </div>
          </div>
          <style>{`
            @keyframes slide-up-sheet {
              from { transform: translateY(100%); }
              to   { transform: translateY(0); }
            }
            .animate-slide-up-sheet { animation: slide-up-sheet 0.28s cubic-bezier(0.16, 1, 0.3, 1) both; }
          `}</style>
        </div>
      )}

      {/* 2026-05-16 — Sort row (mobile parity with desktop /transport).
          Default Created ↓ shows newest first. Tap to flip direction. */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1 -mx-1 px-1" data-testid="to-sort-row">
        <span className="text-[11px] text-slate-500 flex-shrink-0 mr-1">Sort:</span>
        {[
          { id: "created_at",    label: "Created" },
          { id: "date_loading",  label: "Loading" },
          { id: "date_unloading",label: "Unloading" },
          { id: "order_number",  label: "Order #" },
          { id: "price",         label: "Price" },
        ].map((s) => {
          const active = sortBy === s.id;
          return (
            <button
              key={s.id}
              onClick={() => toggleSort(s.id)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap border ${
                active
                  ? "bg-purple-600 text-white border-purple-600"
                  : "bg-white text-slate-600 border-slate-200"
              }`}
              data-testid={`to-sort-${s.id}`}
            >
              {s.label}
              {active && (sortOrder === "desc" ? " ↓" : " ↑")}
            </button>
          );
        })}
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {[
          { id: "active", label: `Active (${orders.filter((o) => !["completed", "pod_received"].includes((o.workflow_status || "").toLowerCase())).length})` },
          { id: "pod_needed", label: "POD needed" },
          { id: "completed", label: "Completed" },
          { id: "all", label: `All (${orders.length})` },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setStatusFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${statusFilter === f.id ? "bg-purple-600 text-white" : "bg-slate-100 text-slate-600"}`}
            data-testid={`to-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Truck className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{search ? "No matching transport orders" : "No transport orders found"}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((o) => (
            <button
              key={o.id}
              onClick={() => { setSelectedOrder(o); setLinkInfo(null); }}
              className="w-full bg-white rounded-xl p-3 border shadow-sm active:bg-slate-50 text-left"
              data-testid={`to-card-${o.order_number}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-800 truncate text-sm">{o.order_number}</span>
                    {o.customer_tracking_token && (
                      <ExternalLink className="w-3 h-3 text-blue-500 flex-shrink-0" />
                    )}
                    {o.pod_file_id && (
                      <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                    )}
                  </div>
                  {o.pf_number && <p className="text-xs text-blue-600 mt-0.5">{o.pf_number}</p>}
                  <p className="text-xs text-slate-500 mt-1 truncate">
                    {o.trucking_company_name || o.trucking_company?.name || "—"} → {o.unloading_place?.split("\n")[0] || "—"}
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400">
                    {o.created_at && (
                      <span title="Created">📅 {new Date(o.created_at).toLocaleDateString()}</span>
                    )}
                    {o.date_loading && (
                      <span title="Loading date">🚚 {String(o.date_loading).slice(0, 10)}</span>
                    )}
                    {o.price != null && o.price !== "" && (
                      <span className="ml-auto font-semibold text-emerald-600">€{o.price}</span>
                    )}
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusBadgeColor(o.workflow_status)} flex-shrink-0`}>
                  {(o.workflow_status || "pending").replace(/_/g, " ")}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Staff Profile Tab
// Weekly Trends (mobile) — PFs opened vs offers sent + email-to-advisors trigger
function StaffTrendsTab({ token }) {
  const [weeks, setWeeks] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailOpen, setEmailOpen] = useState(false);
  const [recipients, setRecipients] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/analytics/weekly-trends?weeks=${weeks}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load trends");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weeks]);

  const send = async () => {
    const list = recipients.split(/[,;\s]+/).map((x) => x.trim()).filter((x) => x.includes("@"));
    if (!list.length) { toast.error("Enter at least one valid email"); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API_URL}/analytics/weekly-trends/email`,
        { weeks, recipients: list, message },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Sent to ${r.data.recipients.join(", ")}`);
      setEmailOpen(false); setRecipients(""); setMessage("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const series = data?.series || [];
  const maxVal = Math.max(1, ...series.map((s) => Math.max(s.pfs_opened, s.offers_sent)));
  const pf = data?.pf_trend || {}, off = data?.offer_trend || {};
  const dirIcon = (d) => d === "up" ? <TrendingUp className="w-3.5 h-3.5" /> : d === "down" ? <TrendingUp className="w-3.5 h-3.5 rotate-180" /> : <span>–</span>;

  return (
    <div className="p-4 space-y-4" data-testid="mobile-trends">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800">Weekly Trends</h2>
        <button onClick={load} data-testid="mobile-trends-refresh" className="p-2 rounded-lg bg-slate-100">
          <RefreshCw className={`w-4 h-4 text-slate-600 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Range selector */}
      <div className="flex gap-1.5">
        {[8, 12, 26, 52].map((w) => (
          <button key={w} onClick={() => setWeeks(w)} data-testid={`mobile-trends-weeks-${w}`}
            className={`flex-1 rounded-lg py-1.5 text-xs font-medium ${weeks === w ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"}`}>
            {w}w
          </button>
        ))}
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-indigo-50 p-3">
              <p className="text-xs font-medium text-indigo-700">PFs opened</p>
              <p className="text-2xl font-bold text-slate-900">{pf.total ?? 0}</p>
              <p className="text-[11px] text-slate-500 flex items-center gap-1">{dirIcon(pf.direction)} avg {pf.avg_per_week ?? 0}/wk</p>
            </div>
            <div className="rounded-xl bg-emerald-50 p-3">
              <p className="text-xs font-medium text-emerald-700">Offers sent</p>
              <p className="text-2xl font-bold text-slate-900">{off.total ?? 0}</p>
              <p className="text-[11px] text-slate-500 flex items-center gap-1">{dirIcon(off.direction)} avg {off.avg_per_week ?? 0}/wk</p>
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-indigo-500" /> PFs opened</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500" /> Offers sent</span>
          </div>

          {/* Mini per-week bars */}
          <div className="rounded-xl border border-slate-100 divide-y">
            {series.map((s) => (
              <div key={s.week_start} className="px-3 py-2" data-testid={`mobile-trend-row-${s.week_start}`}>
                <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                  <span>{s.label}{s.partial ? " (partial)" : ""}</span>
                  <span><b className="text-indigo-600">{s.pfs_opened}</b> / <b className="text-emerald-600">{s.offers_sent}</b></span>
                </div>
                <div className="space-y-1">
                  <div className="h-2 rounded-full bg-indigo-500" style={{ width: `${Math.max(4, (s.pfs_opened / maxVal) * 100)}%` }} />
                  <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${Math.max(4, (s.offers_sent / maxVal) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>

          {/* Email trigger */}
          <button onClick={() => setEmailOpen(true)} data-testid="mobile-trends-email-btn"
            className="w-full bg-indigo-600 text-white rounded-xl py-2.5 text-sm font-medium flex items-center justify-center gap-2">
            <Mail className="w-4 h-4" /> Email report to advisors
          </button>
          <p className="text-[11px] text-slate-400 text-center">Sends a graph, the week-by-week table and a CSV attachment.</p>
        </>
      )}

      {/* Email modal */}
      {emailOpen && (
        <div className="fixed inset-0 z-[120] bg-black/50 flex items-end" onClick={() => setEmailOpen(false)}>
          <div className="bg-white w-full rounded-t-2xl p-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mobile-trends-email-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Mail className="w-5 h-5 text-indigo-600" /> Email this analysis</h3>
              <button onClick={() => setEmailOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <label className="text-xs font-medium text-slate-600">Recipients</label>
            <Input value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="advisor@example.com, second@example.com" className="mb-3 mt-1" data-testid="mobile-trends-recipients" />
            <label className="text-xs font-medium text-slate-600">Message (optional)</label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Add a short note…" className="mb-3 mt-1" data-testid="mobile-trends-message" />
            <button disabled={sending} onClick={send} data-testid="mobile-trends-send"
              className="w-full bg-indigo-600 text-white rounded-lg py-2.5 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Sending…" : `Send (last ${weeks} weeks)`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function StaffProfileTab({ user, onLogout }) {
  return (
    <div className="space-y-6 pb-24">
      <div className="bg-white rounded-2xl p-5 border text-center">
        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
          <User className="w-10 h-10 text-slate-600" />
        </div>
        <h2 className="text-xl font-bold mt-3">{user?.full_name || "Staff"}</h2>
        <p className="text-sm text-slate-500">{user?.email}</p>
        <span className="inline-block mt-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
          Staff
        </span>
      </div>

      {/* Account Info */}
      <div className="bg-white rounded-2xl border overflow-hidden">
        <div className="p-4 border-b">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-slate-500" />
            <div>
              <span className="text-sm text-slate-500">Department</span>
              <p className="font-medium">{user?.department || "Europacific"}</p>
            </div>
          </div>
        </div>
        <div className="p-4 border-b">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-slate-500" />
            <div>
              <span className="text-sm text-slate-500">Email</span>
              <p className="font-medium">{user?.email || "Not set"}</p>
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-slate-500" />
            <div>
              <span className="text-sm text-slate-500">Phone</span>
              <p className="font-medium">{user?.phone || "Not set"}</p>
            </div>
          </div>
        </div>
      </div>

      <Button
        variant="outline"
        className="w-full text-red-600 border-red-200 hover:bg-red-50"
        onClick={onLogout}
        data-testid="staff-logout-btn"
      >
        <LogOut className="w-4 h-4 mr-2" />
        Sign Out
      </Button>
    </div>
  );
}

// Main Staff Mobile App
export default function StaffMobilePage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("home");
  // 2026-05-17 — cross-tab deep-link: PF detail can request the TOs tab
  // to open a specific Transport Order. `pendingOpenTo` is set by
  // PositionFoldersTab, consumed (and cleared) by TransportOrdersTab.
  const [pendingOpenTo, setPendingOpenTo] = useState(null);
  const jumpToTo = (toRef) => {
    setPendingOpenTo(toRef);
    setActiveTab("transport");
  };
  // 2026-05-17 — Reverse deep-link: TO detail can request the PFs tab
  // to open the parent Position Folder of the currently-open TO.
  const [pendingOpenPf, setPendingOpenPf] = useState(null);
  const jumpToPf = (pfRef) => {
    setPendingOpenPf(pfRef);
    setActiveTab("pfs");
  };
  const [stats, setStats] = useState({
    podPending: 0,
    messagesPending: 0,
    waTicketsPending: 0,
    checksScheduled: 0,
    upcomingChecks: [],
    ordersToday: 0,
    completed: 0,
    approvalsPending: 0
  });
  const [loading, setLoading] = useState(true);
  const previousTicketCountRef = useRef(0);

  useEffect(() => {
    fetchStats();
    // Background poll every 45s for new WhatsApp tickets so the badge stays fresh
    const id = setInterval(fetchStats, 45000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchStats = async () => {
    try {
      const [ordersRes, messagesRes, checksRes, ticketsRes, custAppsRes, carrAppsRes] = await Promise.all([
        axios.get(`${API_URL}/staff/mobile/orders-needing-pod`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { orders: [] } })),
        axios.get(`${API_URL}/staff/mobile/pending-messages`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { messages: [] } })),
        axios.get(`${API_URL}/staff/my-customs-checks`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { checks: [] } })),
        axios.get(`${API_URL}/whatsapp/tickets?limit=1&status=pending`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { stats: { pending: 0 } } })),
        axios.get(`${API_URL}/customer-registration?limit=1`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/carrier-vendors?limit=1`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: {} })),
      ]);

      const pendingFromCounts = (sc) => Object.entries(sc || {}).reduce(
        (n, [k, v]) => (["approved", "rejected", "declined"].includes(k) ? n : n + (v || 0)), 0);
      const approvalsPending = pendingFromCounts(custAppsRes.data?.status_counts)
        + pendingFromCounts(carrAppsRes.data?.status_counts);

      const newTicketCount = ticketsRes.data?.stats?.pending || 0;
      // Toast when a new ticket arrives (after initial load)
      if (
        !loading
        && newTicketCount > previousTicketCountRef.current
        && previousTicketCountRef.current >= 0
      ) {
        const delta = newTicketCount - previousTicketCountRef.current;
        toast(`📨 ${delta} new WhatsApp ticket${delta === 1 ? "" : "s"}`, {
          action: { label: "Open", onClick: () => setActiveTab("messages") },
          duration: 8000,
        });
      }
      previousTicketCountRef.current = newTicketCount;

      setStats({
        podPending: ordersRes.data.orders?.length || 0,
        messagesPending: messagesRes.data.messages?.length || 0,
        waTicketsPending: newTicketCount,
        checksScheduled: checksRes.data.checks?.length || 0,
        upcomingChecks: checksRes.data.checks || [],
        ordersToday: 0,
        completed: 0,
        approvalsPending
      });
    } catch (error) {
      console.error("Failed to fetch stats", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Redirect non-staff users to customer mobile app
  if (user?.user_type !== "europacific_staff") {
    navigate("/mobile");
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col relative">
      {/* Top Navigation Bar */}
      <StaffTopBar user={user} activeTab={activeTab} setActiveTab={setActiveTab} approvalsPending={stats.approvalsPending} />
      
      <main
        className="flex-1 px-4 py-4 overflow-auto"
        style={{ paddingBottom: "calc(7rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <AnimatePresence mode="wait">
          {activeTab === "home" && (
            <motion.div
              key="home"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <StaffHomeTab user={user} stats={stats} setActiveTab={setActiveTab} />
            </motion.div>
          )}
          {activeTab === "pfs" && (
            <motion.div
              key="pfs"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <ErrorBoundary pageName="Position Folders (Staff Mobile)">
                <PositionFoldersTab
                  token={token}
                  onJumpToTo={jumpToTo}
                  pendingOpenPf={pendingOpenPf}
                  clearPendingOpenPf={() => setPendingOpenPf(null)}
                />
              </ErrorBoundary>
            </motion.div>
          )}
          {activeTab === "leave" && (
            <motion.div
              key="leave"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <LeaveTab token={token} />
            </motion.div>
          )}
          {activeTab === "pod" && (
            <motion.div
              key="pod"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <PodUploadTab token={token} />
            </motion.div>
          )}
          {activeTab === "transport" && (
            <motion.div
              key="transport"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <TransportOrdersTab
                token={token}
                pendingOpenTo={pendingOpenTo}
                clearPendingOpenTo={() => setPendingOpenTo(null)}
                onJumpToPf={jumpToPf}
              />
            </motion.div>
          )}
          {activeTab === "inspect" && (
            <motion.div
              key="inspect"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <CustomsInspectionTab token={token} />
            </motion.div>
          )}
          {activeTab === "crm" && (
            <motion.div
              key="crm"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <CrmNoteTab token={token} />
            </motion.div>
          )}
          {activeTab === "voice" && (
            <motion.div
              key="voice"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <VoiceNotesTab token={token} />
            </motion.div>
          )}
          {activeTab === "notes" && (
            <motion.div
              key="notes"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <StickyNotesTab token={token} user={user} />
            </motion.div>
          )}
          {activeTab === "messages" && (
            <motion.div
              key="messages"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <StaffMessagesTab token={token} />
            </motion.div>
          )}
          {activeTab === "profile" && (
            <motion.div
              key="profile"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <StaffProfileTab user={user} onLogout={handleLogout} />
            </motion.div>
          )}
          {activeTab === "trends" && (
            <motion.div
              key="trends"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <StaffTrendsTab token={token} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <StaffNavBar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        badges={{
          pod: stats.podPending,
          pfs: stats.checksScheduled,
          messages: stats.waTicketsPending + stats.messagesPending,
        }}
      />

      {/* 2026-05-16 — iOS "Add to Home Screen" nudge banner */}
      <IosInstallBanner appName="EP Staff" accent="emerald" storageKey="ios-install-banner-staff" />
    </div>
  );
}
